import { DioloStamp } from "~/components/storefront/DioloStamp";
import { REWARDS_RETIRED } from "~/services/rewards/rewardsStatus";
import { json, redirect } from "@remix-run/node";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { Link, useLoaderData, useOutletContext } from "@remix-run/react";
import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import prisma from "~/utils/db.server";
import { isSellable, launchLabel } from "~/utils/launch";
import { useNonce } from "~/utils/nonce-provider";
import type { CartItem } from "./_storefront";
import { resolveMarket } from "~/services/markets/marketDetection.server";
import { getMarketPrices } from "~/services/markets/marketPricing.server";
import { convertPrice } from "~/services/currency/currencyService.server";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";
import { ImageLightbox } from "~/components/storefront/ImageLightbox";
import { StarRating } from "~/components/storefront/StarRating";
import { SizeGuideModal } from "~/components/storefront/SizeGuideModal";
import { FabricFeatures } from "~/components/storefront/FabricFeatures";
import { FabricDetails } from "~/components/storefront/FabricDetails";
import { getFabricInfo } from "~/utils/fabricInfo";
import { getColorHex, SIZE_ORDER, fixImgUrl } from "~/utils/storefront-constants";
import { resolveProductMedia, resolveFeaturedImage } from "~/utils/productMedia";
import { formatStorefrontPrice } from "~/utils/money";
import { getProductBaseName, getProductColor, getProductGarment } from "~/utils/productSiblings";
import type { Locale } from "~/utils/locale";
import { localePath, detectLocale } from "~/utils/locale";
import { t } from "~/utils/translations";
import { sanitizeHtml } from "~/utils/sanitize";
import { displayTitle as titleForLocale, renameProductTitle } from "~/utils/productTitle";
import { getOrCreateRetailSessionId } from "~/utils/retailSession";
import { verifyPreviewToken, verifyAdminToken } from "~/services/auth/jwt.server";
import { compareByPieceSlot } from "~/utils/productPieceClassifier";
import { buildRef } from "~/services/messenger/refParser";

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.product) return [{ title: "Product Not Found — Diolo" }];
  const locale = (data as any).locale === "en" ? "en" : "vi";

  // Clean title for search + social previews. Prefer the curated English
  // title on the EN storefront; fall back to the raw title (which still
  // gets the Vietnamese parenthetical stripped by displayTitle). VI stays
  // as-is since the Vietnamese colour is meaningful to VN customers.
  const titleEn = (data.product as any).titleEn as string | null | undefined;
  const cleanTitle = titleForLocale(data.product.title, locale, titleEn);

  // Description: prefer localised descriptionEn on EN, fall back to the raw
  // description. If neither is set we build a sensible marketing default so
  // Google search snippets and social previews aren't empty. Products
  // imported from Sapo often arrive without descriptions.
  const descriptionEn = (data.product as any).descriptionEn as string | null | undefined;
  const rawDesc =
    locale === "en" && descriptionEn && descriptionEn.trim()
      ? descriptionEn
      : data.product.description || "";
  // Strip HTML tags + collapse whitespace for the meta description.
  const plainDesc = rawDesc.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const description = plainDesc
    ? plainDesc.slice(0, 160)
    : locale === "en"
      ? `Shop ${cleanTitle} at Diolo — men's pickleball wear, designed in Vietnam. Free shipping on orders over 1.500.000₫.`
      : `Mua ${cleanTitle} tại Diolo — trang phục pickleball nam, thiết kế tại Việt Nam. Miễn phí vận chuyển cho đơn từ 1.500.000₫.`;

  // T1.3: og:image must be an absolute https URL or social crawlers (Facebook,
  // Slack, Zalo, Twitter) show a broken-image icon. The loader now returns
  // `origin` and a pre-built absolute image URL.
  const image = data.ogImage;
  const canonicalUrl = `${data.origin}/products/${data.product.handle}`;

  // T3.4: JSON-LD Product schema so Google Shopping / rich results get
  // structured data (name, image, price, currency, availability, rating).
  // T3.24: schema URL comes from request origin not a hardcoded domain.
  const price = Number((data.product as any).retailPrice ?? 0);
  const currency = (data as any).currency || "VND";
  const inStock = ((data.product as any).variants || []).some(
    (v: any) => (v.inventoryQuantity ?? 0) > 0,
  );
  const reviewStats = (data as any).reviewStats;
  const productSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: cleanTitle,
    description,
    image,
    sku: (data.product as any).sku || data.product.handle,
    brand: { "@type": "Brand", name: "Diolo" },
    offers: {
      "@type": "Offer",
      url: canonicalUrl,
      priceCurrency: currency,
      // JPY/KRW/VND are 0-decimal currencies — Google Merchant rejects
      // fractional yen/won/dong. Everything else renders with 2 decimals.
      price: price.toFixed(
        currency === "VND" || currency === "JPY" || currency === "KRW" ? 0 : 2,
      ),
      availability: inStock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      // Google Merchant / Rich Results want an Offer validity date; its
      // absence triggers a Search Console "missing field priceValidUntil"
      // warning. We don't model per-sale end dates, so use a rolling +30d.
      priceValidUntil: new Date(Date.now() + 30 * 86400000)
        .toISOString()
        .slice(0, 10),
    },
  };
  if (reviewStats?.count > 0) {
    productSchema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: reviewStats.average.toFixed(1),
      reviewCount: reviewStats.count,
    };
  }
  // Individual Review objects — Google increasingly wants per-review data
  // (not aggregate-only) for star rich results. Emit up to 5 published
  // reviews already loaded by the PDP loader.
  const reviewList = (data as any).reviews as
    | Array<{ author?: string; customerName?: string; rating: number; comment?: string; body?: string; createdAt?: string }>
    | undefined;
  if (reviewList && reviewList.length > 0) {
    productSchema.review = reviewList.slice(0, 5).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author || r.customerName || "Verified Buyer" },
      reviewRating: {
        "@type": "Rating",
        ratingValue: r.rating,
        bestRating: 5,
        worstRating: 1,
      },
      ...(r.comment || r.body ? { reviewBody: r.comment || r.body } : {}),
      ...(r.createdAt ? { datePublished: String(r.createdAt).slice(0, 10) } : {}),
    }));
  }

  // Above-fold image preload — improves PDP LCP by giving the browser
  // a head-start on the cover image. `image` is the absolute ogImage URL
  // already computed in the loader.
  const preloadImage = image
    ? [
        {
          tagName: "link" as const,
          rel: "preload",
          as: "image",
          href: image,
          fetchpriority: "high",
        },
      ]
    : [];

  // Draft preview meta: same tags as a live page MINUS the canonical
  // link (no canonical URL for a draft) and JSON-LD (no rich-result
  // structured data) PLUS noindex/nofollow so search engines don't
  // index the draft view. The ?preview= URL is the only human-facing
  // indicator — no [PREVIEW] in the tab title or og:title because
  // merchant feedback said the URL is already obvious enough.
  const isExplicitPreview = Boolean((data as any).explicitPreview);
  const isDraft = isExplicitPreview && !(data.product as any).isRetailEnabled;
  if (isDraft) {
    return [
      { title: `${cleanTitle} — Diolo` },
      { name: "description", content: description },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: cleanTitle },
      { property: "og:description", content: description },
      { property: "og:image", content: image },
      { property: "og:type", content: "product" },
      { property: "og:locale", content: locale === "en" ? "en_US" : "vi_VN" },
      { property: "og:site_name", content: "Diolo" },
      ...preloadImage,
    ];
  }

  return [
    { title: `${cleanTitle} — Diolo` },
    { name: "description", content: description },
    // T3.2: canonical URL for variant-param deduplication
    { tagName: "link", rel: "canonical", href: canonicalUrl },
    { property: "og:title", content: cleanTitle },
    { property: "og:description", content: description },
    { property: "og:image", content: image },
    { property: "og:type", content: "product" },
    { property: "og:url", content: canonicalUrl },
    { property: "og:locale", content: locale === "en" ? "en_US" : "vi_VN" },
    { property: "og:site_name", content: "Diolo" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: cleanTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    ...preloadImage,
    // T3.4: Product JSON-LD
    { "script:ld+json": productSchema },
  ];
};

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { handle } = params;
  if (!handle) throw new Response("Not Found", { status: 404 });

  const shop = await prisma.shop.findFirst();
  if (!shop) throw new Response("Not Found", { status: 404 });

  // ─── Preview mode detection ─── //
  //
  // Two ways to bypass isRetailEnabled=false:
  //   1. ?preview=<jwt> URL param — EXPLICIT preview. The admin's
  //      "Preview" button generates this. Shareable for sign-off.
  //      Triggers the visible PREVIEW banner so the viewer knows
  //      they're seeing a draft.
  //   2. admin_token cookie present + valid — IMPLICIT preview. An
  //      admin who's just browsing the storefront (no token) can
  //      still land on a draft URL and get the page rendered instead
  //      of a 404. Loads silently — no banner, no [PREVIEW] title,
  //      because this is incidental browsing, not deliberate preview.
  //
  // Both paths drop isRetailEnabled and allow lookup by ID, but only
  // explicitPreview triggers the banner / noindex / draft cache header.
  // status=ACTIVE always required so soft-deleted products stay hidden.
  const url = new URL(request.url);
  const previewTokenParam = url.searchParams.get("preview");
  let previewMode = false;
  let explicitPreview = false; // true ONLY when caller used ?preview= token
  let previewProductId: string | null = null;

  if (previewTokenParam) {
    try {
      const payload = verifyPreviewToken(previewTokenParam);
      if (payload.shopId === shop.id) {
        previewMode = true;
        explicitPreview = true;
        previewProductId = payload.productId;
      }
    } catch {
      // Invalid / expired token — fall through to admin cookie check
    }
  }

  if (!previewMode) {
    const cookieHeader = request.headers.get("Cookie") || "";
    const m = cookieHeader.match(/(?:^|;\s*)admin_token=([^;]*)/);
    if (m && m[1]) {
      try {
        const adminPayload = verifyAdminToken(decodeURIComponent(m[1]));
        if (adminPayload.shopId === shop.id) {
          previewMode = true; // grants access, but explicitPreview stays false
        }
      } catch {
        // Not a valid admin session — strict-mode storefront applies
      }
    }
  }

  // Strict where clause for normal visitors. Preview drops isRetailEnabled
  // and also allows looking up by ID in case the draft has no handle yet.
  const where: any = previewMode
    ? {
        shopId: shop.id,
        OR: [{ handle }, { id: handle }],
        status: "ACTIVE",
        ...(previewProductId ? { id: previewProductId } : {}),
      }
    : {
        shopId: shop.id,
        handle,
        isRetailEnabled: true,
        status: "ACTIVE",
      };

  const product = await prisma.product.findFirst({
    where,
    include: {
      variants: {
        where: previewMode ? undefined : { isAvailable: true },
        orderBy: [{ option1: "asc" }, { option2: "asc" }],
      },
      collection: { select: { name: true } },
      season: { select: { name: true } },
    },
  });

  if (!product) {
    // 2026-05-12: smart 404 redirect.
    //
    // Customers who click a stale Facebook ad land on a product
    // handle that no longer exists in our catalog — historically we
    // threw a hard 404 and the customer bounced (Clarity session
    // 1mooan1 confirmed exactly this pattern at 00:45 of RO-08303's
    // session). Better UX: parse a "line" token out of the handle
    // (e.g. /products/monaco-skorts-old-handle → "monaco") and try
    // to recommend the same line in a related colour, falling back
    // to the all-products listing if nothing matches.
    //
    // We still emit a 302 (not 200) so search engines don't index
    // these dead URLs as duplicates of the listing. Customers see
    // the redirect transparently; SEO is intact.
    const lineCandidate = (handle.split("-")[0] || "").trim();
    if (lineCandidate.length >= 3) {
      const related = await prisma.product.findFirst({
        where: {
          shopId: shop.id,
          isRetailEnabled: true,
          status: "ACTIVE",
          title: { contains: lineCandidate, mode: "insensitive" },
        },
        select: { handle: true },
      });
      if (related?.handle) {
        throw redirect(`/products/${related.handle}`);
      }
      // Fall back to a search on the line keyword so the listing
      // shows everything that line ever had — even if the colour
      // we tried isn't currently active.
      throw redirect(`/products?q=${encodeURIComponent(lineCandidate)}`);
    }
    // No usable token — send them to the full listing.
    throw redirect("/products");
  }

  // ─── Sibling products (color variants with same base name) ─── //
  const baseName = getProductBaseName(product.title);
  const currentColor = getProductColor(product.title);

  // Build "You May Also Like" OR filter (avoid empty OR array)
  const orConditions: { productType?: string; activity?: string }[] = [];
  if (product.productType) orConditions.push({ productType: product.productType });
  if (product.activity) orConditions.push({ activity: product.activity });

  // "Shop the Look" — match line + colour from the title.
  //
  // First version (handle.startsWith("<base>-")) didn't work for
  // products where the piece appears in the MIDDLE of the handle
  // — e.g. monaco-skorts-black, monaco-bra-black. Those don't
  // start with "monaco-black-". And variant.option1 is the SIZE
  // (S/M/L) not the color, so we can't match on that either.
  //
  // Robust approach: extract line + colour from the title (which
  // follows the consistent "Line Optional-Piece - Colour (vi)"
  // pattern) and query for products whose title contains BOTH
  // tokens.
  //
  // Examples (current product → matches):
  //   monaco-skorts-black ("Monaco Skorts - Black (Đen)")
  //     line=Monaco, colour=Black → monaco-bra-black
  //   monaco-black ("Monaco - Black (Đen)")
  //     line=Monaco, colour=Black → monaco-skorts-black, monaco-bra-black
  //   smash-moss-top ("Smash - Moss TOP (Áo Moss)")
  //     line=Smash, colour=Moss → smash-moss, smash-moss-skirt
  //   aura-plum-top ("Aura - Plum TOP")
  //     line=Aura, colour=Plum → aura-plum, aura-plum-skorts
  //
  // Reuses `baseName` + `currentColor` from above (lines 166-167)
  // which already split product.title on " - " using the
  // getProductBaseName / getProductColor utilities.
  // Line = first whitespace-separated word of the base name. Strips
  // any piece word that may have leaked in (e.g. "Monaco Skorts" → "Monaco").
  const lineToken = baseName.split(/\s+/)[0] || "";
  // Colour = strip trailing piece keywords (e.g. "Black TOP" → "Black").
  const colourToken = currentColor
    .replace(
      /\b(top|short|shorts|skirt|skorts|bra|legging|leggings|tights|tank|jacket|pant|pants|bottom|set)\b/gi,
      "",
    )
    .trim();
  const shopTheLookWhere =
    lineToken && colourToken
      ? {
          shopId: shop.id,
          isRetailEnabled: true,
          status: "ACTIVE" as const,
          id: { not: product.id },
          AND: [
            { title: { contains: lineToken, mode: "insensitive" as const } },
            { title: { contains: colourToken, mode: "insensitive" as const } },
          ],
        }
      : null;

  // Run all queries in parallel for performance
  const [completeTheLook, youMayAlsoLike, reviews, siblingProducts] = await Promise.all([
    // "Shop the Look" — strict line+color matching (see above).
    // orderBy sortPosition mirrors the listing pages so a paired group
    // (Bra + Skorts of same colour) renders Top → Bottom — the
    // sortPosition values were set Top-first by scripts/pair-products-v2
    // and by the action-handler auto-handle backfill. Tie-break on title
    // for stable rendering across deploys.
    shopTheLookWhere
      ? prisma.product.findMany({
          where: shopTheLookWhere,
          include: {
            variants: {
              where: { isAvailable: true },
              select: { id: true, option1: true, option2: true, price: true, compareAtPrice: true, inventoryQuantity: true },
            },
          },
          orderBy: [{ sortPosition: "asc" }, { title: "asc" }],
          take: 8,
        })
      : Promise.resolve([]),
    // "You May Also Like" — same product type or activity
    orConditions.length > 0
      ? prisma.product.findMany({
          where: {
            shopId: shop.id,
            isRetailEnabled: true,
            status: "ACTIVE",
            id: { not: product.id },
            OR: orConditions,
          },
          include: {
            variants: {
              where: { isAvailable: true },
              select: { id: true, option1: true, option2: true, price: true, compareAtPrice: true, inventoryQuantity: true },
            },
          },
          orderBy: [{ sortPosition: "asc" }, { title: "asc" }],
          take: 8,
        })
      : Promise.resolve([]),
    // Reviews — select only public fields to prevent leaking customerEmail
    prisma.productReview.findMany({
      where: { productId: product.id, isPublished: true },
      orderBy: { createdAt: "desc" },
      take: 20,
      select: {
        id: true,
        customerName: true,
        rating: true,
        title: true,
        body: true,
        isVerified: true,
        helpfulCount: true,
        notHelpfulCount: true,
        createdAt: true,
      },
    }),
    // Sibling products (other colors of the same base product)
    currentColor
      ? prisma.product.findMany({
          where: {
            shopId: shop.id,
            isRetailEnabled: true,
            status: "ACTIVE",
            id: { not: product.id },
            title: { startsWith: baseName + " - " },
          },
          select: {
            id: true,
            title: true,
            handle: true,
            featuredImageUrl: true,
          },
          take: 12,
        })
      : Promise.resolve([]),
  ]);

  // ─── Accessory recommendations (2026-05-12) ───
  //
  // Merchant feedback: the strict line+color "Shop the Look" matches
  // are great for outfit-completion but miss two upsell anchors
  // customers reach for regardless of the outfit's colorway:
  //
  //   • A white sock — applies to EVERY PDP.
  //   • Visor hats   — applies to court-sport PDPs (tennis / pickleball).
  //                    Anything with `product.activity` matching court
  //                    qualifies; the canonical value is "court" but
  //                    legacy/tag values "tennis" + "pickleball" are
  //                    accepted too in case of inconsistent tagging.
  //
  // Both surfaces match on title substrings (`sock`/`vớ`, `visor`,
  // `white`/`trắng`) with case-insensitive `mode: 'insensitive'` so it
  // works regardless of capitalisation. Matches both `title` and
  // `titleEn` so Vietnamese-titled rows don't slip through.
  //
  // The results are merged into `completeTheLook` (after dedup by id
  // and excluding the current product) — they appear AFTER the strict
  // outfit matches in the same carousel rather than as a separate
  // section. The display cap was bumped from slice(0, 6) → slice(0, 10)
  // in the render path so accessories don't get squeezed out by a
  // strong outfit-match set.
  const isCourtSport = !!product.activity && /court|tennis|pickleball/i.test(product.activity);
  const accessoryWhere: any = {
    shopId: shop.id,
    isRetailEnabled: true,
    status: "ACTIVE",
    id: { not: product.id },
    OR: [
      // White sock — always
      {
        AND: [
          {
            OR: [
              { title: { contains: "sock", mode: "insensitive" as const } },
              { title: { contains: "vớ", mode: "insensitive" as const } },
              { titleEn: { contains: "sock", mode: "insensitive" as const } },
            ],
          },
          {
            OR: [
              { title: { contains: "white", mode: "insensitive" as const } },
              { title: { contains: "trắng", mode: "insensitive" as const } },
              { titleEn: { contains: "white", mode: "insensitive" as const } },
            ],
          },
        ],
      },
      // Visor hats — only on court-sport PDPs
      ...(isCourtSport
        ? [
            {
              OR: [
                { title: { contains: "visor", mode: "insensitive" as const } },
                { titleEn: { contains: "visor", mode: "insensitive" as const } },
              ],
            },
          ]
        : []),
    ],
  };
  const accessoryProducts = await prisma.product.findMany({
    where: accessoryWhere,
    include: {
      variants: {
        where: { isAvailable: true },
        select: {
          id: true,
          option1: true,
          option2: true,
          price: true,
          compareAtPrice: true,
          inventoryQuantity: true,
        },
      },
    },
    take: 6,
  });

  // Merge accessories into completeTheLook with id-based dedup. Strict
  // outfit matches come first; accessories tack on after. Mutating a
  // local rebinding is safer than `.push` on `completeTheLook` since
  // downstream code captures it by reference.
  const completeTheLookSeenIds = new Set(
    (completeTheLook as Array<{ id: string }>).map((p) => p.id),
  );
  for (const acc of accessoryProducts) {
    if (!completeTheLookSeenIds.has(acc.id)) {
      (completeTheLook as Array<any>).push(acc);
      completeTheLookSeenIds.add(acc.id);
    }
  }

  const reviewStats = {
    count: reviews.length,
    average: reviews.length > 0
      ? Math.round((reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length) * 10) / 10
      : 0,
    distribution: [5, 4, 3, 2, 1].map((star) => ({
      star,
      count: reviews.filter((r) => r.rating === star).length,
    })),
  };

  // ─── Apply market pricing if visitor is in a non-base-currency market ─── //
  const { market } = await resolveMarket(request, shop.id);
  const shopCurrency = shop.currency || "VND";

  if (market && market.currency !== shopCurrency) {
    // The main product query filters variants by `isAvailable: true`, so a
    // fully sold-out product comes back with `product.variants = []`. That
    // would skip market pricing entirely and leak the raw VND retailPrice
    // into the JSON-LD + visible price with the foreign currency label —
    // e.g. an OOS product would show "550000.00 SGD". We still need intl
    // pricing on the sold-out PDP (customers click through from search /
    // old links), so fetch all variants separately for the lookup when
    // the visible set is empty.
    let pricingVariants: Array<{ id: string; price: unknown; compareAtPrice?: unknown }> =
      product.variants;
    if (pricingVariants.length === 0) {
      pricingVariants = await prisma.productVariant.findMany({
        where: { productId: product.id },
        select: { id: true, price: true, compareAtPrice: true },
      });
    }

    // Collect all variant IDs (product + related products)
    const allVariants = [
      ...pricingVariants.map((v: any) => ({
        id: v.id,
        price: Number(v.price),
        compareAtPrice: v.compareAtPrice ? Number(v.compareAtPrice) : null,
      })),
      ...completeTheLook.flatMap((p: any) =>
        p.variants.map((v: any) => ({
          id: v.id,
          price: Number(v.price),
          compareAtPrice: v.compareAtPrice ? Number(v.compareAtPrice) : null,
        }))
      ),
      ...youMayAlsoLike.flatMap((p: any) =>
        p.variants.map((v: any) => ({
          id: v.id,
          price: Number(v.price),
          compareAtPrice: v.compareAtPrice ? Number(v.compareAtPrice) : null,
        }))
      ),
    ];

    if (allVariants.length > 0) {
      const marketPriceMap = await getMarketPrices(
        allVariants,
        market.marketId,
        shopCurrency,
        shop.id
      );

      // Apply market prices to product variants
      for (const v of product.variants as any[]) {
        const mp = marketPriceMap.get(v.id);
        if (mp) {
          v.price = mp.price;
          v.compareAtPrice = mp.compareAtPrice;
        }
      }
      // Also update product-level retail price from the first available
      // variant's market price. Falls back to pricingVariants[0] when the
      // UI-visible set is empty (sold-out product) so JSON-LD + currency
      // display stay correct.
      const firstVariantForPrice =
        (product.variants[0] as any) ?? (pricingVariants[0] as any);
      if (firstVariantForPrice) {
        const firstMp = marketPriceMap.get(firstVariantForPrice.id);
        if (firstMp) {
          (product as any).retailPrice = firstMp.price;
          (product as any).retailComparePrice = firstMp.compareAtPrice;
        }
      }

      // Apply to related products (completeTheLook + youMayAlsoLike).
      // IMPORTANT: the rendering in the JSX does
      //   `item.retailPrice ? item.retailPrice : variants[0].price`
      // so mutating only `v.price` isn't enough — if the DB has a raw VND
      // retailPrice, it wins the ternary and the sibling card shows a
      // VND number with the market's currency label (e.g. "550000 SGD").
      // Mirror the main product logic: after updating variants, also
      // replace the related product's retailPrice with the first
      // available variant's market price. Fixes audit finding B2.
      //
      // Fallback for SOLD-OUT related products (audit 2026-04-25):
      // when every variant has `isAvailable: false` the loader filter
      // returns `p.variants = []`, the loop above does nothing, and
      // the raw VND `p.retailPrice` leaks into the card with the
      // market currency symbol — e.g. "Muse - Tea Rose £850,000.00".
      //
      // Resolution priority (audit 2026-04-25 — second pass):
      //   1. Any MarketPrice row for this product in the active
      //      market (regardless of variant availability)
      //   2. Pivot through SG: look up the SG MarketPrice and
      //      FX-convert SGD→target. SG is the international base
      //      currency; the merchant set explicit SGD prices for every
      //      product and other markets are derived from it.
      //   3. Last resort: hide the price (null) — never fall back to
      //      VND conversion, because VN pricing is exclusive to the
      //      Vietnam market and is intentionally cheaper than the
      //      international tier (per merchant policy 2026-04-25).
      const intlFallbackProductIds = new Set<string>();
      for (const p of [...completeTheLook, ...youMayAlsoLike] as any[]) {
        for (const v of p.variants) {
          const mp = marketPriceMap.get(v.id);
          if (mp) {
            v.price = mp.price;
            v.compareAtPrice = mp.compareAtPrice;
          }
        }
        const firstRelatedMp =
          p.variants[0] && marketPriceMap.get(p.variants[0].id);
        if (firstRelatedMp) {
          p.retailPrice = firstRelatedMp.price;
          p.retailComparePrice = firstRelatedMp.compareAtPrice;
        } else if (market.currency !== shopCurrency) {
          intlFallbackProductIds.add(p.id);
        }
      }

      if (intlFallbackProductIds.size > 0) {
        const productIds = [...intlFallbackProductIds];
        // Single batched lookup: one query for target-market prices,
        // one query for SG fallback prices. Avoids N+1.
        const [targetMps, sgMps] = await Promise.all([
          prisma.marketPrice.findMany({
            where: { marketId: market.marketId, productId: { in: productIds } },
            select: { productId: true, price: true, compareAtPrice: true },
          }),
          market.marketId === "market-singapore"
            ? Promise.resolve([])
            : prisma.marketPrice.findMany({
                where: {
                  marketId: "market-singapore",
                  productId: { in: productIds },
                },
                select: { productId: true, price: true, compareAtPrice: true },
              }),
        ]);
        const targetMap = new Map(
          targetMps.filter((m) => m.productId).map((m) => [m.productId!, m]),
        );
        const sgMap = new Map(
          sgMps.filter((m) => m.productId).map((m) => [m.productId!, m]),
        );

        for (const p of [...completeTheLook, ...youMayAlsoLike] as any[]) {
          if (!intlFallbackProductIds.has(p.id)) continue;
          const direct = targetMap.get(p.id);
          if (direct) {
            p.retailPrice = Number(direct.price);
            p.retailComparePrice = direct.compareAtPrice
              ? Number(direct.compareAtPrice)
              : null;
            continue;
          }
          const sg = sgMap.get(p.id);
          if (sg) {
            p.retailPrice = await convertPrice(
              Number(sg.price),
              "SGD",
              market.currency,
              shop.id,
            );
            p.retailComparePrice = sg.compareAtPrice
              ? await convertPrice(
                  Number(sg.compareAtPrice),
                  "SGD",
                  market.currency,
                  shop.id,
                )
              : null;
            continue;
          }
          // No SG either — hide the price rather than display the
          // cheap VN-converted amount (which would mis-represent
          // international value).
          p.retailPrice = null;
          p.retailComparePrice = null;
        }
      }
    }
  }

  // Build siblings list with parsed color info. FIX 2026-06-07: filter to the
  // SAME GARMENT as the current product. Several lines embed the garment in the
  // post-dash segment ("Smash - Black TOP", "Smash - Black Skirt"), so the
  // line-prefix sibling query above (startsWith "Smash - ") pulls in BOTH tops
  // and skirts — the PDP then showed cross-garment "colour" swatches (clicking
  // a swatch switched garment type) with duplicate/polluted chips. Also dedupe
  // by cleaned colour so we never render two "Black" dots.
  const currentGarment = getProductGarment(product.title);
  const seenSiblingColors = new Set<string>([currentColor.toLowerCase()]);
  const siblings = siblingProducts
    .filter((s: any) => {
      if (currentGarment && getProductGarment(s.title) !== currentGarment) return false;
      const c = getProductColor(s.title);
      if (!c) return false;
      const key = c.toLowerCase();
      if (seenSiblingColors.has(key)) return false;
      seenSiblingColors.add(key);
      return true;
    })
    .map((s: any) => ({
      handle: s.handle,
      color: getProductColor(s.title),
      featuredImageUrl: s.featuredImageUrl,
      title: s.title,
    }));

  const currency = market?.currency || shopCurrency;

  // T1.3: build absolute origin + og:image URL server-side so meta functions
  // can emit an https:// URL. Social crawlers reject relative paths.
  // Fly terminates TLS at the edge — `request.url` on the inside is http://,
  // so we force the https scheme since production is always HTTPS.
  const origin = new URL(request.url).origin.replace(/^http:\/\//, "https://");
  // Resolve the cover image via the shared priority helper so products with
  // only backfilled Shopify images also get a real og:image — not a logo.
  const resolvedCover = resolveFeaturedImage(product as any);
  const ogImage = resolvedCover
    ? (resolvedCover.startsWith("http") ? resolvedCover : `${origin}${resolvedCover.startsWith("/") ? "" : "/"}${resolvedCover}`)
    : `${origin}/og/diolo-v2.png`;

  // Audit 2026-05-16: server-side ViewContent CAPI used to fire here
  // from the loader. That had two compounding bugs:
  //   1. Privacy: enriching this response with logged-in customer PII
  //      would have leaked it via the s-maxage=300 CDN cache below —
  //      the next visitor on this PDP would receive the previous
  //      customer's identity fields baked into the HTML.
  //   2. Under-reporting: crypto.randomUUID() ran once per cache MISS,
  //      meaning every visitor served from cache shared the same event_id.
  //      Meta dedup then merged dozens of separate sessions into one
  //      counted event — at typical PDP traffic we were losing ~90% of
  //      ViewContent volume.
  // ViewContent CAPI is now fired client-side from useEffect (see further
  // down this component) via POST /api/fb-capi with a session-unique
  // event_id, mirroring the AddToCart pattern in _storefront.tsx.
  // /api/fb-capi enriches the event with the logged-in customer's full
  // identity (em/ph/fn/ln/ct/st/zp/country/external_id) plus the
  // persistent eql_visitor_id cookie as external_id fallback for guests.

  // T3.34: serve PDP with a short CDN cache so repeat hits don't touch the
  // DB. `s-maxage` lets Cloudflare cache at the edge; `stale-while-revalidate`
  // keeps the response fresh while a background revalidation runs.
  // Detect locale from request (cookie or Accept-Language) so the meta
  // function can pick locale-appropriate title + fallback description
  // without needing to read outlet context (which isn't available in meta).
  const locale = detectLocale(request.url, request.headers.get("Cookie") || "");

  // Diolo display rename: "Monaco Skorts" → "The Monaco Skorts",
  // "Monaco Bra" → "The Monaco Top". Applied AFTER Shop-the-Look
  // matching (which extracts tokens from the raw Sapo title) so we
  // don't break companion-piece detection. See utils/productTitle.ts.
  const renameItem = (item: any) => {
    if (!item) return item;
    item.title = renameProductTitle(item.title);
    if (item.titleEn) item.titleEn = renameProductTitle(item.titleEn);
    return item;
  };
  renameItem(product);
  for (const item of completeTheLook as any[]) renameItem(item);
  for (const item of youMayAlsoLike as any[]) renameItem(item);
  for (const sib of siblings as any[]) {
    if (sib?.title) sib.title = renameProductTitle(sib.title);
  }

  // Belt-and-braces site-wide Top → Bottom ordering. The DB orderBy
  // already gives the right sequence for properly-paired sortPositions,
  // but for any out-of-sync data (newly imported products that haven't
  // been re-paired yet) a final piece-slot sort guarantees the
  // merchant's invariant: Tops always render before matching Bottoms.
  // Stable: only re-orders items that the DB layer already grouped
  // together (Array.sort with identical keys keeps original order).
  (completeTheLook as any[]).sort(compareByPieceSlot);
  (youMayAlsoLike as any[]).sort(compareByPieceSlot);

  // Cache policy is driven by EXPLICIT preview (token in URL), not the
  // admin-cookie convenience path. Admin browsing a draft via cookie
  // gets the same private/no-store response (we can't risk caching a
  // draft view), but everywhere else (including admin viewing live
  // PDPs) goes through the public CDN cache.
  const isDraftView = previewMode && !(product as any).isRetailEnabled;
  const cacheHeader = isDraftView
    ? "private, no-store, max-age=0"
    : "public, max-age=60, s-maxage=300, stale-while-revalidate=3600";

  return json(
    {
      sellable: isSellable(request.headers.get("Cookie")),
      product,
      completeTheLook,
      youMayAlsoLike,
      reviews,
      reviewStats,
      siblings,
      currentColor,
      currency,
      origin,
      ogImage,
      locale,
      previewMode,
      explicitPreview,
      // Messenger deep-link config — drives the "Message us about this
      // product" button (added 2026-05-26). Null = button hidden.
      facebookPageUsername: (shop as any).facebookPageUsername || null,
      instagramHandle: (shop as any).instagramHandle || null,
    },
    {
      headers: {
        "Cache-Control": cacheHeader,
      },
    },
  );
};

const FIT_LABELS: Record<string, string> = {
  TRUE_TO_SIZE: "True to size",
  RUNS_SMALL: "Runs small \u2014 size up",
  RUNS_LARGE: "Runs large \u2014 size down",
};

const ACTIVITY_ICONS: Record<string, { icon: string; label: string }> = {
  tennis: { icon: "M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5", label: "Tennis & Pickleball" },
  yoga: { icon: "M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z", label: "Yoga & Pilates" },
  lounge: { icon: "M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25", label: "Lounge & Everyday" },
  gym: { icon: "M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z", label: "Gym & Training" },
};

export default function ProductDetail() {
  // Defensive guard — Sentry (REMIX-11) caught
  // "Cannot destructure property 'product' from null or undefined value"
  // 12 times in 10 days. The loader always returns json({...}) on the
  // happy path and throws Response/redirect for not-found, so
  // useLoaderData should never legitimately be null. The crash happens
  // during a transition / error-boundary edge case where the component
  // mounts briefly with no data. Return null so the next render with
  // real data (or the ErrorBoundary, whichever wins) is what the user
  // sees, instead of a blank crash.
  const loaderData = useLoaderData<typeof loader>();
  // Pre-launch gate — false until 09.09 21:00 unless this visitor holds the preview cookie.
  const preLaunch = !loaderData.sellable;
  if (!loaderData) return null;
  const { product, completeTheLook, youMayAlsoLike, reviews, reviewStats, siblings, currentColor, currency, origin } = loaderData;
  const previewMode = Boolean((loaderData as any).previewMode);
  const explicitPreview = Boolean((loaderData as any).explicitPreview);
  // Messenger config (added 2026-05-26 for PDP→Messenger context handoff).
  // Null when the merchant hasn't seeded Shop.facebookPageUsername yet —
  // the button hides cleanly so a half-configured rollout doesn't break.
  const facebookPageUsername = (loaderData as any).facebookPageUsername as string | null;
  const instagramHandle = (loaderData as any).instagramHandle as string | null;
  const nonce = useNonce();
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<any>(null);
  const { addToCart, wishlist, toggleWishlist, cartOpen, locale: outletLocale, retailCustomer, effectiveFreeShippingThreshold, market, membership } = useOutletContext<{
    addToCart: (item: CartItem) => void;
    wishlist?: string[];
    toggleWishlist?: (productId: string) => void;
    cartOpen?: boolean;
    locale?: Locale;
    membership?: { enabled: boolean; unitLabel: { vi: string; en: string } } | null;
    retailCustomer?: {
      firstName: string;
      email: string;
      messengerLinkToken?: string | null;
    } | null;
    effectiveFreeShippingThreshold?: number;
    market?: {
      marketId: string;
      marketName: string;
      currency: string;
      country: string;
      taxRate?: number | null;
      taxInclusive?: boolean;
    } | null;
  }>();
  const locale: Locale = outletLocale || "vi";

  // Tax-display hint per-market (fixes audit finding: PDP/cart showed
  // no "Inc VAT/GST" note on markets where Shopify Markets displays
  // inclusive prices, and no "Taxes at checkout" on the rest). Shows
  // nothing on the primary VN market since tax rules are implicit there.
  const taxNote: string | null = (() => {
    if (!market || market.currency === "VND") return null;
    if (market.taxInclusive) {
      if (market.country === "GB") return locale === "vi" ? "Đã bao gồm VAT" : "Includes VAT";
      if (market.country === "AU") return locale === "vi" ? "Đã bao gồm GST" : "Includes GST";
      return locale === "vi" ? "Đã bao gồm thuế" : "Taxes included";
    }
    return locale === "vi" ? "Thuế & vận chuyển tính tại thanh toán" : "Taxes & shipping calculated at checkout";
  })();
  // Lifetime Set Membership hint (config-gated; null when the program is
  // off or this product is flagged as not earning a stamp).
  const stampHint: string | null = (() => {
    if (!membership?.enabled) return null;
    if ((product as { earnsStamps?: boolean }).earnsStamps === false) return null;
    const unit = locale === "vi" ? membership.unitLabel.vi : membership.unitLabel.en;
    return locale === "vi" ? `+1 ${unit} thành viên` : `Earns +1 membership ${unit}`;
  })();
  const isWishlisted = wishlist?.includes(product.id) ?? false;

  // ─── Meta Pixel + CAPI: ViewContent (client-side, deduplicated via eventID) ─── //
  // Audit 2026-05-16: eventID is now generated CLIENT-SIDE per page
  // mount so each session gets a unique ID — fixes the
  // multi-session-shares-one-eventID dedup bug caused by the previous
  // server-side UUID generation being cached by Cloudflare. Mirrors the
  // AddToCart pattern in _storefront.tsx: fire fbq pixel + POST /api/fb-capi
  // with the same event_id. /api/fb-capi enriches with the logged-in
  // customer's full identity (em/ph/fn/ln/ct/st/zp/country/external_id)
  // plus the persistent eql_visitor_id cookie as external_id for guests.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const productPrice = Number((product as any).retailPrice ?? product.variants?.[0]?.price ?? 0);
    const eventPayload = {
      content_ids: [product.id],
      content_type: "product_group",
      content_name: product.title,
      value: productPrice,
      currency: currency || "VND",
    };
    const vcEventId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `vc_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    if (typeof (window as any).fbq === "function") {
      (window as any).fbq("track", "ViewContent", eventPayload, { eventID: vcEventId });
    }
    // Server-side CAPI mirror — enriched with logged-in customer
    // identity (or visitor_id for guests) by the /api/fb-capi handler.
    fetch("/api/fb-capi", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_name: "ViewContent",
        event_id: vcEventId,
        source_url: window.location.href,
        custom_data: eventPayload,
      }),
      keepalive: true,
    }).catch(() => {});
    // First-party telemetry (2026-05-12): client-side write so the
    // count isn't undercut by Cloudflare's PDP s-maxage cache (a
    // cache hit skips the loader, so a server-side write would miss
    // every repeat view).
    fetch("/api/retail-events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_name: "view_content",
        product_id: product.id,
        price: productPrice,
        value: productPrice,
        currency: currency || "VND",
        session_id: getOrCreateRetailSessionId(),
        page_url: window.location.pathname + window.location.search,
      }),
      keepalive: true,
    }).catch(() => {});
  }, [product.id, product.title, currency]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bilingual title — delegates to titleForLocale (utils/productTitle.ts)
  // which on EN strips the trailing "(Vietnamese)" parenthetical and on VI
  // swaps the English colour for the Vietnamese one inside the parens.
  const displayTitle = titleForLocale(product.title, locale, (product as any).titleEn);
  const rawDescription = locale === "en" && (product as any).descriptionEn ? (product as any).descriptionEn : product.description;
  const displayDescription = rawDescription ? sanitizeHtml(rawDescription) : "";

  // Detect if option1 contains sizes (not colors) — happens when product has only one option
  const option1IsSizeOnly = useMemo(() => {
    const hasNoOption2 = product.variants.every((v) => !v.option2);
    if (!hasNoOption2) return false;
    const opt1Values = [...new Set(product.variants.map((v) => v.option1).filter(Boolean))] as string[];
    if (opt1Values.length === 0) return false;
    // Filter out known placeholder values BEFORE the all-sizes check.
    // Without this, a single dirty variant with option1="Default Title"
    // (Sapo's default name for the first variant before sizes are
    // assigned — seen on Monaco Black/Espresso Bras + Skorts before
    // the 2026-05-25 data fix) breaks the every-is-size detection.
    // option1IsSizeOnly then returns false → the colour picker
    // renders the SIZE strings (S/M/L/XL) as fake greyed-out colour
    // swatches above the actual size picker.
    const meaningful = opt1Values.filter(
      (v) => !/^(default title|freesize|free size|one size|onesize|os|n\/a)$/i.test(v),
    );
    if (meaningful.length === 0) return false;
    return meaningful.every((v) => v in SIZE_ORDER);
  }, [product.variants]);

  // Get unique colors and sizes — remap if option1 is actually sizes
  const colors = useMemo(() => {
    if (option1IsSizeOnly) return []; // No colors for size-only products
    const seen = new Set<string>();
    return product.variants
      .filter((v) => v.option1 && !seen.has(v.option1) && seen.add(v.option1))
      .map((v) => v.option1!)
      // Merchant feedback 2026-05-13: Sapo imports accessories with a
      // single "Default Title" variant (and sometimes "Freesize" /
      // "One Size"). Without this filter, accessory PDPs rendered a
      // ghost "Default Title" colour swatch above an "ATC disabled
      // until you pick a size" button — visually broken AND blocked
      // purchases. Strip those placeholders here so the color picker
      // and the colour-name label only ever show real colours.
      .filter((v) => !/^(default title|freesize|free size|one size|onesize|os|n\/a)$/i.test(v));
  }, [product.variants, option1IsSizeOnly]);

  const sizes = useMemo(() => {
    const seen = new Set<string>();
    let raw: string[];
    if (option1IsSizeOnly) {
      // Sizes are in option1
      raw = product.variants
        .filter((v) => v.option1 && !seen.has(v.option1) && seen.add(v.option1))
        .map((v) => v.option1!);
    } else {
      raw = product.variants
        .filter((v) => v.option2 && !seen.has(v.option2) && seen.add(v.option2))
        .map((v) => v.option2!);
    }
    // 2026-05-25: filter known Sapo placeholders before showing them
    // in the picker. The option1IsSizeOnly heuristic already tolerates
    // a stray "Default Title" so the size-vs-colour detection is
    // correct, but the PLACEHOLDER value itself was still landing in
    // the sizes array and rendering as a pickable option. Customers
    // could click "Default Title" thinking it's a weird size code,
    // and the cart would happily attach the dirty variant's FK —
    // exactly the failure mode behind RO-08347 (Smash Brown TOP/Skirt
    // shipped S because customer picked the dirty-data variant).
    // Belt-and-braces: even after the 2026-05-25 data patch cleaned
    // 129 variants, this filter prevents a future dirty Sapo import
    // from re-introducing the bug silently.
    return raw
      .filter((v) => !/^(default title|freesize|free size|one size|onesize|os|n\/a)$/i.test(v))
      .sort((a, b) => {
        const orderA = SIZE_ORDER[a] ?? 50;
        const orderB = SIZE_ORDER[b] ?? 50;
        if (orderA !== orderB) return orderA - orderB;
        return a.localeCompare(b);
      });
  }, [product.variants, option1IsSizeOnly]);

  // ─── No-size-axis detection (2026-05-13) ─────────────────────────
  // Accessories (visor, socks, caps, tumblers) have a single variant
  // with option1="Default Title" or option1="Freesize" — there's no
  // real size to pick. Before this guard, the PDP rendered a "Select
  // a size" button that never enabled, leaving the ATC button
  // permanently disabled and customers literally unable to buy socks
  // or visors. Merchant flagged 2026-05-13 with screenshots.
  //
  // True when:
  //   - product has variants AND
  //   - no real size axis was extracted (sizes.length === 0), OR
  //   - the single "size" extracted is a placeholder like
  //     "Default Title" / "Freesize" / "One Size" / "OS".
  //
  // When true, downstream code:
  //   - auto-selects the first variant
  //   - skips the size-selector UI
  //   - drops the !selectedSize guard from the ATC button
  const hasNoSizeAxis = useMemo(() => {
    if (product.variants.length === 0) return false;
    if (sizes.length === 0) return true;
    if (sizes.length === 1) {
      return /^(default title|freesize|free size|one size|onesize|os|n\/a)$/i.test(sizes[0]);
    }
    return false;
  }, [product.variants, sizes]);

  const [selectedColor, setSelectedColor] = useState(colors[0] || "");
  // Aritzia-style hover preview: shows the colour name beside "Colour" while
  // the shopper hovers a swatch (without committing the selection). Cleared
  // on mouse-leave so the label falls back to the actual selected colour.
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState("");
  // Quantity selector. Capped at the selected variant's inventory.
  // Resets to 1 when the size (and therefore variant) changes — see effect below.
  const [quantity, setQuantity] = useState(1);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<Set<string>>(() => new Set(["details"]));
  // Size guide modal — opens "Find my size" quiz + chart (added 2026-04-30)
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  // Which view the size-guide modal opens into ("quiz" = Find-my-size).
  const [sizeGuideMode, setSizeGuideMode] = useState<"chart" | "quiz">("chart");
  const [openDropdown, setOpenDropdown] = useState<"colour" | "size" | null>(null);
  const [showDesktopStickyBar, setShowDesktopStickyBar] = useState(false);

  // Notify-me-when-back-in-stock state. Lives on the PDP because the form is
  // rendered in two places (main column + sticky bar) and they need to share
  // a single submit/status lifecycle so the user only sees one toast.
  // The endpoint is POST /api/back-in-stock — see app/routes/api.back-in-stock.ts.
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyStatus, setNotifyStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [notifyError, setNotifyError] = useState("");

  const toggleSection = useCallback(
    (name: string, event?: React.MouseEvent<HTMLButtonElement>) => {
      const triggerEl = event?.currentTarget;
      setOpenSections((prev) => {
        const next = new Set(prev);
        const wasOpen = next.has(name);
        if (wasOpen) next.delete(name);
        else next.add(name);
        // Merchant feedback 2026-05-13: clicking "Shipping & Returns"
        // on the PDP expanded the section but the content got cut
        // off because the right column is `lg:sticky lg:top-24` — a
        // sticky element can't grow taller than the viewport, so the
        // bottom of the expanded accordion ends up below the fold and
        // the customer has to scroll down on the LEFT (image) column
        // to see the expanded text. Scrolling the trigger into view
        // on open makes the new content visible without forcing the
        // user to scroll manually.
        if (!wasOpen && triggerEl) {
          // rAF so the DOM has time to render the expanded content
          // before we measure / scroll.
          requestAnimationFrame(() => {
            // Find the enclosing accordion (trigger + content) and
            // scroll its bottom edge into view so the expanded text
            // is visible.
            const accordion = triggerEl.closest(".aritzia-accordion");
            (accordion ?? triggerEl).scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
          });
        }
        return next;
      });
    },
    [],
  );

  // Reset state when navigating between products (Remix reuses the component)
  useEffect(() => {
    setSelectedColor(colors[0] || "");
    setSelectedSize("");
    setCurrentImageIndex(0);
    setOpenAccordion(null);
    setOpenSections(new Set(["details"]));
    setOpenDropdown(null);
    setShowDesktopStickyBar(false);
    setShowReviewForm(false);
    setReviewSearch("");
    setReviewSort("relevant");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  // ─── Save to Recently Viewed (localStorage) ─── //
  useEffect(() => {
    try {
      const key = "eql_recently_viewed";
      const stored = JSON.parse(localStorage.getItem(key) || "[]") as Array<{
        id: string;
        handle: string;
        title: string;
        titleEn?: string | null;
        featuredImageUrl: string | null;
        price: number;
      }>;
      const filtered = stored.filter((p) => p.id !== product.id);
      // Use the resolved cover (not raw `featuredImageUrl`) so products
      // that rely on `backupImages` still have a thumbnail in the
      // Recently Viewed rail.
      const cover = resolveFeaturedImage(product as any) || product.featuredImageUrl;
      filtered.unshift({
        id: product.id,
        handle: product.handle || product.id,
        title: product.title,
        // Save the curated English title alongside the raw Vietnamese title
        // so RecentlyViewed can render the right one for the active locale
        // without ever needing a server round-trip. Pre-existing entries
        // without titleEn fall back to title (matching old behaviour).
        titleEn: (product as any).titleEn ?? null,
        featuredImageUrl: cover || null,
        price: Number(product.retailPrice || product.variants[0]?.price || 0),
      });
      localStorage.setItem(key, JSON.stringify(filtered.slice(0, 12)));
    } catch {
      // localStorage unavailable (SSR / private mode)
    }
  }, [product]);

  // Close dropdown when cart opens
  useEffect(() => {
    if (cartOpen) setOpenDropdown(null);
  }, [cartOpen]);

  // Lock body scroll when dropdown is open
  useEffect(() => {
    if (openDropdown) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [openDropdown]);

  // Escape key closes dropdown
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    if (openDropdown) {
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    }
  }, [openDropdown]);

  // Pin the info column only when it fits in the viewport below the header
  // (top-24 = 96px). A sticky column taller than the window can't be scrolled
  // to its bottom until the gallery on the left has scrolled all the way
  // past — Hayden hit exactly that on desktop reading the details. Measured
  // live (accordions change the height), SSR renders unpinned.
  const infoColumnRef = useRef<HTMLDivElement>(null);
  const [infoColumnPins, setInfoColumnPins] = useState(false);
  useEffect(() => {
    const el = infoColumnRef.current;
    if (!el || typeof window === "undefined") return;
    const check = () => setInfoColumnPins(el.offsetHeight + 96 + 24 <= window.innerHeight);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener("resize", check);
    return () => { ro.disconnect(); window.removeEventListener("resize", check); };
  }, []);

  // Desktop sticky add-to-bag bar — appears once the reviews section reaches the viewport
  useEffect(() => {
    const check = () => {
      const target = document.getElementById("reviews");
      if (!target) return;
      const rect = target.getBoundingClientRect();
      // Show the bar once the top of the reviews section enters the bottom 80% of the viewport,
      // and keep it visible while the user continues scrolling past it.
      const trigger = window.innerHeight * 0.8;
      setShowDesktopStickyBar(rect.top <= trigger);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [product.id]);

  // Mobile gallery state
  const mobileGalleryRef = useRef<HTMLDivElement>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Mobile gallery → full-screen pinch/swipe lightbox (tap any frame to open).
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Color thumbnails state
  const [showAllColors, setShowAllColors] = useState(false);

  // Review search/sort state
  const [reviewSearch, setReviewSearch] = useState("");
  // T4.6: default sort was "relevant" but the underlying logic is
  // verified-first + newest-first — which is "verified" in UI terms, not
  // relevance. Renamed the value so future readers aren't confused.
  const [reviewSort, setReviewSort] = useState("verified");
  // "Was this helpful?" votes — optimistic deltas over loader counts, with
  // a localStorage guard so each browser votes once per review.
  const [reviewVotes, setReviewVotes] = useState<
    Record<string, { up: number; down: number; voted?: "up" | "down" }>
  >({});
  useEffect(() => {
    // Hydrate the voted-guard so buttons render disabled on revisit.
    try {
      const stored = JSON.parse(localStorage.getItem("diolo_review_votes") || "{}");
      const seed: Record<string, { up: number; down: number; voted?: "up" | "down" }> = {};
      for (const [id, dir] of Object.entries(stored)) {
        if (dir === "up" || dir === "down") seed[id] = { up: 0, down: 0, voted: dir };
      }
      if (Object.keys(seed).length > 0) setReviewVotes(seed);
    } catch {
      /* ignore corrupt storage */
    }
  }, [product.id]);
  const handleReviewVote = useCallback((reviewId: string, dir: "up" | "down") => {
    setReviewVotes((prev) => {
      if (prev[reviewId]?.voted) return prev; // guard double-click
      return {
        ...prev,
        [reviewId]: {
          up: dir === "up" ? 1 : 0,
          down: dir === "down" ? 1 : 0,
          voted: dir,
        },
      };
    });
    try {
      const stored = JSON.parse(localStorage.getItem("diolo_review_votes") || "{}");
      if (stored[reviewId]) return; // already voted in a previous session
      stored[reviewId] = dir;
      localStorage.setItem("diolo_review_votes", JSON.stringify(stored));
    } catch {
      /* storage unavailable — vote still counts server-side */
    }
    void fetch("/api/review-vote", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reviewId, vote: dir }),
    }).catch(() => {});
  }, []);

  // Get images for the selected color (or all images).
  // Uses the shared media resolver so a product whose Sapo gallery is
  // empty or thin (0-1 images) still surfaces the backfilled Shopify
  // images. The per-color filter is applied as a post-step — if no
  // images carry a color tag, it's a no-op.
  const images = useMemo(() => {
    const resolved = resolveProductMedia(product as any);
    const fallback = product.title || "";
    if (resolved.length === 0) return [];
    // Map into the legacy `{url, altText, color}` shape used elsewhere
    // in this file so we don't have to rewire a lot of downstream code.
    const fixed = resolved.map((m) => ({
      url: m.url,
      altText: m.alt || fallback,
      color: m.color ?? null,
    }));
    // Show images for selected color first, then the rest
    const colorImages = fixed.filter((i) => i.color === selectedColor);
    const otherImages = fixed.filter((i) => i.color !== selectedColor);
    const combined = colorImages.length > 0 ? [...colorImages, ...otherImages] : fixed;

    // Final URL dedup — prevents duplicate rounds when the same image
    // exists with and without color tags, or when CDN query params differ.
    const seen = new Set<string>();
    return combined.filter((img) => {
      // Normalize: strip query params so "img.jpg?v=1" and "img.jpg" match
      let key = img.url;
      try {
        const u = new URL(img.url);
        key = u.origin + u.pathname;
      } catch { /* use raw url */ }
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [product, selectedColor]);

  // Map each color to its first image (for color thumbnails)
  const colorImageMap = useMemo(() => {
    const raw = product.images as (string | { url: string; color?: string | null })[] | null;
    if (!raw) return {};
    const map: Record<string, string> = {};
    for (const img of raw) {
      if (typeof img === "string") continue;
      if (img.color && !map[img.color]) {
        map[img.color] = fixImgUrl(img.url);
      }
    }
    return map;
  }, [product.images]);

  // Check if a size is in stock for the selected color
  const isSizeAvailable = (size: string) => {
    if (option1IsSizeOnly) {
      return product.variants.some((v) => v.option1 === size && v.inventoryQuantity > 0);
    }
    return product.variants.some(
      (v) => v.option1 === selectedColor && v.option2 === size && v.inventoryQuantity > 0
    );
  };

  // Remaining stock for a size (respecting the selected colour) — for the
  // Aritzia-style "only N left" scarcity cue on the size picker. Mirrors
  // isSizeAvailable's option1-vs-option2 handling.
  const sizeStock = (size: string): number => {
    const matches = option1IsSizeOnly
      ? product.variants.filter((v) => v.option1 === size)
      : product.variants.filter((v) => v.option1 === selectedColor && v.option2 === size);
    return matches.reduce((sum, v) => sum + Math.max(0, v.inventoryQuantity), 0);
  };
  // Show "only N left" once a size is down to this many units or fewer.
  const LOW_STOCK_SIZE_THRESHOLD = 5;
  const lowStockLabel = (n: number) =>
    locale === "vi" ? `Chỉ còn ${n}` : `Only ${n} left`;

  // Get the selected variant
  const selectedVariant = useMemo(() => {
    // No-size accessories (visor/socks/caps): the first variant is the
    // only variant — auto-select it so ATC works without forcing the
    // user to pick a non-existent size.
    if (hasNoSizeAxis) {
      return product.variants[0];
    }
    if (option1IsSizeOnly) {
      return product.variants.find((v) => v.option1 === selectedSize);
    }
    return product.variants.find(
      (v) => v.option1 === selectedColor && v.option2 === selectedSize
    );
  }, [product.variants, selectedColor, selectedSize, option1IsSizeOnly, hasNoSizeAxis]);

  const price = product.retailPrice ? Number(product.retailPrice) : Number(selectedVariant?.price || product.variants[0]?.price || 0);
  const comparePrice = product.retailComparePrice ? Number(product.retailComparePrice) : null;

  // Rewards points calculation — 1 point per 100 VND (1% back), matches
  // POINTS_PER_VND in rewardsService.server.ts. Previously showed 1/1000
  // which was wrong. Hide on non-VND markets until the multi-currency
  // points display is wired through the PDP (it already works on the
  // checkout page). See audit P2.2 + user spec.
  const rewardsPoints = currency === "VND" ? Math.floor(price * 0.01) : 0;

  // Check total stock for IN STOCK / LOW STOCK indicator
  const totalStock = product.variants.reduce((sum, v) => sum + v.inventoryQuantity, 0);
  const stockStatus = totalStock > 20 ? "in_stock" : totalStock > 0 ? "low_stock" : "out_of_stock";

  // Cap on the quantity stepper for the currently selected variant.
  // When no size is picked yet, fall back to the max of all variants
  // (so the stepper isn't disabled before a size is chosen).
  const selectedVariantStock = selectedVariant?.inventoryQuantity ?? 0;
  const maxQuantity = Math.max(
    1,
    selectedVariant
      ? selectedVariantStock
      : Math.max(...product.variants.map((v) => v.inventoryQuantity ?? 0), 1),
  );

  // Reset quantity when the user changes size — different variants have
  // different inventory caps, so a stale qty from the previous variant
  // could exceed stock on the new one.
  useEffect(() => {
    setQuantity(1);
  }, [selectedSize]);

  const handleAddToCart = useCallback(() => {
    if (preLaunch) return; // pre-launch: nothing sellable until 09.09
    // No-size accessories: skip the selectedSize gate, the variant is
    // already auto-selected. Apparel: require a chosen size.
    if (!selectedVariant) return;
    if (!hasNoSizeAxis && !selectedSize) return;
    const effectiveSize = hasNoSizeAxis ? "" : selectedSize;
    const effectiveVariantTitle = hasNoSizeAxis
      ? (selectedColor || selectedVariant.option1 || "")
      : selectedColor
        ? `${selectedColor} / ${selectedSize}`
        : selectedSize;
    addToCart({
      variantId: selectedVariant.id,
      productHandle: product.handle || product.id,
      productTitle: product.title,
      productTitleEn: (product as any).titleEn ?? null,
      variantTitle: effectiveVariantTitle,
      color: selectedColor,
      size: effectiveSize,
      price,
      comparePrice: comparePrice || undefined,
      imageUrl: images[0]?.url,
      quantity: Math.max(1, Math.min(quantity, maxQuantity)),
      productId: product.id,
      // Inventory cap captured at add time so subsequent +/− cart
      // controls can't push above stock.
      maxQuantity: selectedVariant.inventoryQuantity,
    });
    setOpenDropdown(null);
  }, [selectedVariant, selectedSize, selectedColor, hasNoSizeAxis, price, comparePrice, images, product, addToCart, quantity, maxQuantity]);

  // ─── Messenger handoff ─────────────────────────────────────────
  // Opens m.me/<page>?ref=<encoded product+variant> in a new tab so
  // CS receives the conversation already tagged with product context.
  // Instagram users (UA includes "Instagram") get the ig.me/m fallback
  // — IG doesn't honour `ref` webhooks, so we pre-populate the
  // customer's first message with the product name + URL instead.
  // Fires fbq Contact for ad attribution.
  const handleMessengerClick = useCallback(
    (params: {
      productHandle: string;
      productId: string;
      productTitle: string;
      variantSku: string | null;
      facebookPageUsername: string;
      instagramHandle: string | null;
      /** The market-resolved price the customer is looking at right
       *  now (S$28 for a Singapore visitor, 500.000đ for VN). Encoded
       *  into the ref so the webhook's auto-reply quotes the EXACT
       *  number the customer just saw — without this, an SG visitor
       *  would get a Vietnamese-format VND auto-reply, which is jarring. */
      price: number;
      /** ISO 4217 code that pairs with `price` for the same reason. */
      currency: string;
      /** HMAC-signed bridge token from the logged-in customer's
       *  session. The webhook validates it and atomically links the
       *  resulting PSID to the RetailCustomer row. Null/undefined
       *  for anonymous visitors — they fall back to the phone-regex
       *  matching path on the webhook side. */
      messengerLinkToken?: string | null;
    }) => {
      const isIg =
        typeof navigator !== "undefined" && /Instagram/i.test(navigator.userAgent);

      // Pixel event — drives attribution from ad creative → DM lead.
      if (typeof (window as any).fbq === "function") {
        (window as any).fbq("track", "Contact", {
          content_ids: [params.productId],
          content_name: params.productTitle,
        });
      }

      // Optional source token folds in fbclid / utm_source so the
      // webhook can correlate the DM back to the ad that drove it.
      let source: string | undefined;
      try {
        const url = new URL(window.location.href);
        source =
          url.searchParams.get("fbclid") ||
          url.searchParams.get("utm_source") ||
          undefined;
      } catch {
        /* no-op */
      }

      if (isIg && params.instagramHandle) {
        const productUrl = `${window.location.origin}/products/${params.productHandle}`;
        const text =
          locale === "vi"
            ? `Chào shop, mình quan tâm sản phẩm: ${params.productTitle} — ${productUrl}`
            : `Hi! I'm interested in: ${params.productTitle} — ${productUrl}`;
        window.open(
          `https://ig.me/m/${params.instagramHandle}?text=${encodeURIComponent(text)}`,
          "_blank",
          "noopener,noreferrer",
        );
        return;
      }

      const ref = buildRef({
        productHandle: params.productHandle,
        variantSku: params.variantSku,
        source,
        // Encode locale so the webhook's v2 auto-reply picks the
        // right language template ("vi" → Vietnamese, "en" → English).
        // Defaults to "vi" because the storefront's primary market is VN
        // and the few EN-locale visitors are mostly bilingual.
        locale: locale === "en" ? "en" : "vi",
        // Pass the market-resolved price so the auto-reply can echo
        // back the EXACT price the customer saw (otherwise an SG
        // visitor would get "500.000đ" in the auto-reply for a product
        // they were looking at as "S$28" — confusing + wrong).
        price: params.price,
        currency: params.currency,
        // Logged-in retail customers carry an HMAC token through the
        // ref so the webhook can deterministically link the resulting
        // PSID back to their Customer row — no phone-regex required.
        // Null for anonymous visitors; they fall back to the phone-
        // matching path on the webhook side.
        customerToken: params.messengerLinkToken ?? null,
      });
      window.open(
        `https://m.me/${params.facebookPageUsername}?ref=${encodeURIComponent(ref)}`,
        "_blank",
        "noopener,noreferrer",
      );
    },
    [locale],
  );

  // ─── Back-in-stock notification ─────────────────────────────────
  // The loader filters variants to `isAvailable: true`, so most OOS cases
  // surface as `product.variants.length === 0` (whole product sold out).
  // A specific variant being OOS while the product still has other in-stock
  // variants is rarer (continue-selling policy), but we handle it too.
  //
  // Pre-order override (2026-05-26): when the merchant has flipped
  // `preOrderEnabled` on the product, customers can buy regardless of
  // stock — the order writes through to Sapo, fulfilment ships when
  // inventory restocks. So we treat the product as "always orderable"
  // for ATC purposes (button enabled, Notify-Me form hidden). The
  // checkout-side stock validator at _storefront.checkout.tsx:592
  // already short-circuits on the same flag, so the order succeeds
  // end-to-end without a stock guard interfering.
  const preOrderEnabled = Boolean((product as any).preOrderEnabled);
  const rawIsProductFullyOOS = product.variants.length === 0;
  const rawIsVariantOOS = !!(selectedSize && selectedVariant && selectedVariantStock <= 0);
  const isProductFullyOOS = !preOrderEnabled && rawIsProductFullyOOS;
  const isVariantOOS = !preOrderEnabled && rawIsVariantOOS;
  const showNotifyMe = !preOrderEnabled && (rawIsProductFullyOOS || rawIsVariantOOS);

  // Reset the notify form state when the user changes their selection so a
  // success/error message from a previous variant doesn't carry over.
  useEffect(() => {
    setNotifyStatus("idle");
    setNotifyError("");
  }, [selectedSize, selectedColor]);

  const handleNotifySubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const email = notifyEmail.trim();
      if (!email) return;
      setNotifyStatus("loading");
      setNotifyError("");
      try {
        const res = await fetch("/api/back-in-stock", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            productId: product.id,
            variantId: selectedVariant?.id ?? null,
            size: selectedSize || null,
            color: selectedColor || null,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || `Request failed (${res.status})`);
        }
        setNotifyStatus("success");
      } catch (err) {
        setNotifyStatus("error");
        setNotifyError(err instanceof Error ? err.message : "Please try again");
      }
    },
    [notifyEmail, product.id, selectedVariant, selectedSize, selectedColor],
  );

  // Filtered and sorted reviews
  const filteredReviews = useMemo(() => {
    let result = [...reviews];
    if (reviewSearch.trim()) {
      const query = reviewSearch.toLowerCase();
      result = result.filter(
        (r) =>
          r.title?.toLowerCase().includes(query) ||
          r.body?.toLowerCase().includes(query) ||
          r.customerName?.toLowerCase().includes(query)
      );
    }
    switch (reviewSort) {
      case "newest":
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case "highest":
        result.sort((a, b) => b.rating - a.rating);
        break;
      case "lowest":
        result.sort((a, b) => a.rating - b.rating);
        break;
      case "verified":
      default:
        // T4.6: verified-first, then newest-first. This is NOT relevance
        // ranking (which would need TF-IDF on the query) — just a
        // credibility-weighted chronological sort.
        result.sort((a, b) => {
          if (a.isVerified !== b.isVerified) return a.isVerified ? -1 : 1;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
        break;
    }
    return result;
  }, [reviews, reviewSearch, reviewSort]);

  // T4.5: fit guidance from real product data only. The previous version
  // fabricated comfort/fit/quality scores by adding arbitrary offsets to
  // the overall review average ("avg + 0.15" for quality, etc) which is
  // misleading to customers — those bars aren't measuring anything. We now
  // surface only the fit-guide label which comes from the product admin
  // (`product.fitGuideTop`), rendered as a text row without a fake bar.
  const fitLabel = useMemo(() => {
    switch (product.fitGuideTop) {
      case "TRUE_TO_SIZE":
        return "True to size";
      case "RUNS_SMALL":
        return "Runs small — size up";
      case "RUNS_LARGE":
        return "Runs large — size down";
      default:
        return null;
    }
  }, [product.fitGuideTop]);

  // Mobile gallery navigation
  const scrollToImage = (index: number) => {
    const el = mobileGalleryRef.current;
    if (!el) return;
    const child = el.children[index] as HTMLElement;
    if (child) {
      child.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      setCurrentImageIndex(index);
    }
  };

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: locale === "vi" ? "Sản phẩm" : "Products", to: "/products" },
    ...(product.activity
      ? [{
          label: product.activity.charAt(0).toUpperCase() + product.activity.slice(1),
          to: `/products?activity=${encodeURIComponent(product.activity)}`,
        }]
      : product.collection
        ? [{ label: product.collection.name }]
        : []),
    { label: displayTitle },
  ];

  // Structured data: Product JSON-LD
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: displayTitle,
    description: product.description?.replace(/<[^>]*>/g, "").slice(0, 500) || "",
    image: images[0]?.url || product.featuredImageUrl || "",
    brand: { "@type": "Brand", name: "Diolo" },
    offers: {
      "@type": "Offer",
      url: `${origin}/products/${product.handle}`,
      priceCurrency: currency || "VND",
      // Match the formatting used by the loader meta at line 54 — Google
      // Merchant is strict about decimal precision (e.g. it expects
      // "3.70" not "3.7" for USD/SGD/GBP; JPY/KRW/VND have 0 decimals).
      price: Number(price).toFixed(
        currency === "VND" || currency === "JPY" || currency === "KRW" ? 0 : 2,
      ),
      availability: stockStatus === "out_of_stock"
        ? "https://schema.org/OutOfStock"
        : "https://schema.org/InStock",
    },
    ...(reviewStats.count > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: reviewStats.average,
            reviewCount: reviewStats.count,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {}),
  };

  return (
    <div>
      {/* Preview banner removed 2026-05-25: the ?preview= URL token
          is itself the indicator that you're looking at a draft, so
          the amber chrome on top was redundant. Under-the-hood draft
          protections (noindex meta, no-store cache header, JSON-LD
          suppression) still apply — those are for crawlers/CDNs that
          don't read the URL the way humans do. */}

      {/* Structured Data JSON-LD — skipped only when this is an
          explicit draft preview (token + isRetailEnabled=false). Every
          other case (live products, admin-cookie browsing of any
          product) emits JSON-LD normally. */}
      {explicitPreview && !(product as any).isRetailEnabled ? null : (
        <script
          type="application/ld+json"
          nonce={nonce}
          dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd) }}
        />
      )}

      {/* Breadcrumb navigation */}
      <div className="hidden lg:block mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-4">
        <Breadcrumbs locale={locale} items={breadcrumbItems} />
      </div>

      {/* Product — Aritzia layout: 735px image + 44px gap + info */}
      <div className="mx-auto max-w-[1440px] px-0 sm:px-6 lg:px-10 pb-16">
        <div className="lg:grid lg:grid-cols-[735px_1fr] lg:gap-[44px]">
          {/* Left: Image Gallery */}
          <div>
            {/* Desktop: Single image carousel with arrows + zoom on hover */}
            <DesktopImageGallery images={images} productTitle={displayTitle} />

            {/* Mobile: Full-width carousel (Represent style) */}
            <div className="lg:hidden">
              <div
                ref={mobileGalleryRef}
                className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  const index = Math.max(0, Math.min(Math.round(el.scrollLeft / el.clientWidth), images.length - 1));
                  setCurrentImageIndex(index);
                }}
              >
                {images.map((img, i) => (
                  <div
                    key={i}
                    className="flex-none w-full snap-start aspect-[1920/2623] overflow-hidden bg-[#f5f5f5]"
                  >
                    <img
                      src={img.url}
                      alt={img.altText || product.title}
                      className="w-full h-full object-cover cursor-zoom-in"
                      loading={i < 2 ? "eager" : "lazy"}
                      onClick={() => setLightboxIndex(i)}
                      // Backstop: hide a deleted/404 image so the gray
                      // placeholder shows instead of a broken-image icon.
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Dot indicators — below image (Aritzia: small, sharp) */}
              {/* Title + dot indicators row (mobile) */}
              {/* Audit 2026-04-30: changed from <h1> to <h2> so the page has a
                  single semantic <h1> (the desktop one further down). Mobile
                  users still see a styled heading; screen readers announce
                  it as h2 instead of a duplicate h1. */}
              <div className="flex items-center justify-between px-4 py-3 lg:hidden">
                <h2 className="text-base font-normal text-black leading-snug truncate mr-3">
                  {displayTitle}
                </h2>
                {images.length > 1 && (
                  <div className="flex gap-1.5 flex-shrink-0">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => scrollToImage(i)}
                        aria-label={`Go to image ${i + 1}`}
                        className={`w-1.5 h-1.5 transition-colors ${
                          i === currentImageIndex ? "bg-black" : "bg-[#e0e0e0]"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Product Info */}
          <div
            ref={infoColumnRef}
            className={`mt-0 lg:mt-0 lg:self-start ${infoColumnPins ? "lg:sticky lg:top-24" : ""}`}
          >

            {/* ─── Mobile Product Info (Aritzia style) ─── */}
            <div className="lg:hidden px-4 pt-1 pb-32">

              {/* Price */}
              <div className="text-base font-normal text-black flex items-center flex-wrap gap-x-2 gap-y-1">
                {comparePrice ? (
                  <>
                    <span className="text-[#c8102e]">{formatStorefrontPrice(price, currency)}</span>
                    <span className="text-[#767676] line-through">{formatStorefrontPrice(comparePrice, currency)}</span>
                    {Number(comparePrice) > Number(price) && (
                      <span className="px-1.5 py-0.5 bg-black text-white text-[10px] font-normal uppercase tracking-[0.08em]">
                        {locale === "vi"
                          ? `Tiết kiệm ${Math.round((1 - Number(price) / Number(comparePrice)) * 100)}%`
                          : `Save ${Math.round((1 - Number(price) / Number(comparePrice)) * 100)}%`}
                      </span>
                    )}
                  </>
                ) : (
                  <span>{formatStorefrontPrice(price, currency)}</span>
                )}
              </div>
              {taxNote && (
                <p className="mt-1 text-[11px] text-[#767676]">{taxNote}</p>
              )}
              {stampHint && (
                <p className="mt-1 text-[11px] text-[#767676] flex items-center gap-1.5"><DioloStamp className="h-3 w-auto text-black" />{stampHint}</p>
              )}

              {/* Star Rating */}
              {reviewStats.count > 0 && (
                <button
                  onClick={() => document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-3 inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                >
                  <StarRating rating={reviewStats.average} size="sm" />
                  <span className="text-xs text-[#767676] underline">
                    {reviewStats.count} {reviewStats.count === 1 ? t("reviewSingular", locale) : t("reviewPlural", locale)}
                  </span>
                </button>
              )}

              {/* Rewards Points — pin locale to avoid SSR/CSR hydration
                  mismatch (Node default = en-US "6,500" vs VI browser
                  default = vi-VN "6.500"). React errors #418/#425. */}
              {!REWARDS_RETIRED && rewardsPoints > 0 && (
                <p className="text-[11px] text-[#767676] mt-3">
                  {locale === "vi"
                    ? `Nhận ${rewardsPoints.toLocaleString("vi-VN")} điểm Diolo Rewards`
                    : `Earn ${rewardsPoints.toLocaleString("en-US")} Diolo Rewards points`}
                </p>
              )}

              {/* Color Sibling Swatches (mobile) — circular Aritzia style */}
              {(siblings.length > 0 || colors.length > 1) && (
                <div className="mt-6">
                  <p className="text-[11px] font-normal text-black uppercase tracking-[0.08em] mb-2">
                    {locale === "vi" ? "Màu sắc" : "Colour"}
                    {(hoveredColor || currentColor) && (
                      <span className="ml-2 text-[#767676] normal-case tracking-normal">
                        {hoveredColor || currentColor}
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {currentColor && (
                      <span
                        className="sf-color-swatch sf-color-swatch-selected"
                        style={{ ['--swatch-color' as any]: getColorHex(currentColor) }}
                        title={currentColor}
                        onMouseEnter={() => setHoveredColor(currentColor)}
                        onMouseLeave={() => setHoveredColor(null)}
                      />
                    )}
                    {colors.filter(c => c !== currentColor).map((color) => (
                      <button
                        key={color}
                        onClick={() => {
                          setSelectedColor(color);
                          setSelectedSize("");
                        }}
                        onMouseEnter={() => setHoveredColor(color)}
                        onMouseLeave={() => setHoveredColor(null)}
                        className="sf-color-swatch"
                        style={{ ['--swatch-color' as any]: getColorHex(color) }}
                        title={color}
                        aria-label={`Select color ${color}`}
                      />
                    ))}
                    {siblings.map((sib: any) => (
                      <Link
                        key={sib.handle}
                        to={localePath(`/products/${sib.handle}`, locale)}
                        // Prefetch the sibling PDP's loader data + assets
                        // on hover/focus. Customer feedback (Clarity, last
                        // 3 days): 59.38% of sessions had "quick back" —
                        // customers were back-clicking between colorway
                        // PDPs to compare. With `intent` prefetch, hovering
                        // a swatch pre-warms the next PDP so the click
                        // feels near-instant. A full inline state-swap is
                        // planned for a later session — this is the 95%
                        // UX win for 5% of the lift.
                        prefetch="intent"
                        className="sf-color-swatch"
                        style={{ ['--swatch-color' as any]: getColorHex(sib.color) }}
                        title={sib.color}
                        aria-label={`View ${sib.color}`}
                        onMouseEnter={() => setHoveredColor(sib.color)}
                        onMouseLeave={() => setHoveredColor(null)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Notify-me (mobile) — the desktop column has its own render;
                  without this one, tapping an OOS size in the mobile sheet
                  flipped the buy bar to Sold Out with no way to leave an
                  email (2026-06-25). Shares state with the desktop form. */}
              {showNotifyMe && (
                <div className="mt-5 border border-[#e0e0e0] p-4">
                  {notifyStatus === "success" ? (
                    <p className="text-xs text-[#0a7d3e]" role="status">
                      {locale === "vi"
                        ? "Đã ghi nhận. Chúng tôi sẽ gửi email khi sản phẩm có hàng trở lại."
                        : "You're on the list. We'll email you the moment it's back."}
                    </p>
                  ) : (
                    <form onSubmit={handleNotifySubmit}>
                      <p className="text-[11px] uppercase tracking-[0.08em] text-black mb-3">
                        {locale === "vi" ? "Thông báo khi có hàng" : "Notify Me When Available"}
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          required
                          aria-label={locale === "vi" ? "Email của bạn" : "Your email"}
                          placeholder={locale === "vi" ? "Email của bạn" : "your@email.com"}
                          value={notifyEmail}
                          onChange={(e) => setNotifyEmail(e.target.value)}
                          disabled={notifyStatus === "loading"}
                          className="flex-1 h-10 px-3 text-base sm:text-xs border border-[#e0e0e0] focus:outline-none focus:border-black disabled:bg-[#f9f9f9]"
                        />
                        <button
                          type="submit"
                          disabled={notifyStatus === "loading" || !notifyEmail.trim()}
                          className="h-10 px-4 bg-black text-white text-[11px] uppercase tracking-[0.08em] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#1a1a1a] transition-colors"
                        >
                          {notifyStatus === "loading"
                            ? (locale === "vi" ? "Đang gửi..." : "Sending...")
                            : (locale === "vi" ? "Đăng ký" : "Notify Me")}
                        </button>
                      </div>
                      {notifyStatus === "error" && (
                        <p className="mt-2 text-[11px] text-[#c8102e]" role="alert">
                          {locale === "vi"
                            ? "Không gửi được. Vui lòng thử lại."
                            : (notifyError || "Couldn't submit — please try again.")}
                        </p>
                      )}
                    </form>
                  )}
                </div>
              )}

              {/* Description was rendered here previously — removed
                  2026-05-06 per merchant feedback. The Details accordion
                  below already renders description + fabricContent + care
                  instructions, so the top render was a duplicate that
                  pushed the buy controls down without adding info. */}

              {/* Fabric features row — Vuori-inspired (added 2026-04-30).
                  4 small icon+label cards showing the fabric's properties.
                  Selected per product based on `product.fabric`; falls back
                  to a generic set when fabric is null. */}
              <FabricFeatures fabric={product.fabric} productType={product.productType} locale={locale} />

              {/* Accordions: Fabric / Details / Size & Fit / In-store / Shipping / Help.
                  Merchant feedback 2026-05-14: pulled Fabric into its own
                  accordion so the PDP info stack reads as a single, tidy
                  list rather than (icon strip → big fabric block → list).
                  Customers who want fabric copy still open it; those who
                  don't aren't asked to scroll past it. */}
              <div className="mt-8 border-t border-[#e0e0e0]">
                {/* Fabric — gated by `product.fabric`. The accordion shell
                    is rendered ONLY if FabricDetails would have content
                    (i.e. fabric value maps to a known fabric in
                    fabricInfo.ts); otherwise we skip the row entirely to
                    avoid an empty toggle on accessories. */}
                {getFabricInfo(product.fabric) && (
                  <div className="aritzia-accordion">
                    <button
                      type="button"
                      onClick={(e) => toggleSection("fabric", e)}
                      className="aritzia-accordion-trigger"
                      aria-expanded={openSections.has("fabric")}
                    >
                      <span>{locale === "vi" ? "Chất liệu" : "Fabric"}</span>
                      <span className="aritzia-accordion-icon">{openSections.has("fabric") ? "−" : "+"}</span>
                    </button>
                    {openSections.has("fabric") && (
                      <div className="aritzia-accordion-content">
                        <FabricDetails fabric={product.fabric} locale={locale} variant="accordion" />
                      </div>
                    )}
                  </div>
                )}

                {/* Details */}
                <div className="aritzia-accordion">
                  <button
                    type="button"
                    onClick={(e) => toggleSection("details", e)}
                    className="aritzia-accordion-trigger"
                    aria-expanded={openSections.has("details")}
                  >
                    <span>{locale === "vi" ? "Chi tiết" : "Details"}</span>
                    <span className="aritzia-accordion-icon">{openSections.has("details") ? "−" : "+"}</span>
                  </button>
                  {openSections.has("details") && (
                    <div className="aritzia-accordion-content space-y-3">
                      {displayDescription && (
                        <div className="prose prose-sm max-w-none [&_*]:!text-xs [&_*]:!text-black [&_*]:!font-normal" dangerouslySetInnerHTML={{ __html: displayDescription }} />
                      )}
                      {(() => {
                        // Locale-aware fabric content. Vietnamese storefront
                        // prefers fabricContentVi; falls back to English copy
                        // if the translation is empty so a missing translation
                        // never blanks the composition line. EN locale stays
                        // on the English field (which is the canonical input).
                        const fabric =
                          locale === "vi"
                            ? ((product as any).fabricContentVi || product.fabricContent)
                            : product.fabricContent;
                        if (!fabric) return null;
                        return (
                          <p className="text-xs text-black">
                            <span className="font-normal">{locale === "vi" ? "Thành phần: " : "Composition: "}</span>{fabric}
                          </p>
                        );
                      })()}
                      {product.careInstructions && (
                        <p className="text-xs text-black">
                          <span className="font-normal">{locale === "vi" ? "Bảo quản: " : "Care: "}</span>{product.careInstructions}
                        </p>
                      )}
                      {product.styleNumber && (
                        <p className="text-xs text-[#767676]">
                          <span className="font-normal">{locale === "vi" ? "Mã kiểu: " : "Style: "}</span>{product.styleNumber}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Size & Fit */}
                <div className="aritzia-accordion">
                  <button
                    type="button"
                    onClick={(e) => toggleSection("fit", e)}
                    className="aritzia-accordion-trigger"
                    aria-expanded={openSections.has("fit")}
                  >
                    <span>{locale === "vi" ? "Kích cỡ & form dáng" : "Size & Fit"}</span>
                    <span className="aritzia-accordion-icon">{openSections.has("fit") ? "−" : "+"}</span>
                  </button>
                  {openSections.has("fit") && (
                    <div className="aritzia-accordion-content">
                      {product.fitGuideTop && (
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] uppercase tracking-[0.08em] text-[#767676]">{locale === "vi" ? "Chật" : "Runs Small"}</span>
                            <span className="text-[10px] uppercase tracking-[0.08em] text-[#767676]">{locale === "vi" ? "Đúng size" : "True to Size"}</span>
                            <span className="text-[10px] uppercase tracking-[0.08em] text-[#767676]">{locale === "vi" ? "Rộng" : "Runs Large"}</span>
                          </div>
                          <div className="relative h-[2px] bg-[#e0e0e0]">
                            <div
                              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-black"
                              style={{
                                left: product.fitGuideTop === "RUNS_SMALL" ? "15%"
                                  : product.fitGuideTop === "RUNS_LARGE" ? "85%"
                                  : "50%",
                                transform: "translate(-50%, -50%)",
                              }}
                            />
                          </div>
                          <p className="text-[11px] text-black mt-2 text-center">
                            {FIT_LABELS[product.fitGuideTop] || product.fitGuideTop}
                          </p>
                        </div>
                      )}
                      {product.fitGuideBottom && !FIT_LABELS[product.fitGuideBottom] && (
                        <p>{product.fitGuideBottom}</p>
                      )}
                      {!product.fitGuideTop && !product.fitGuideBottom && (
                        // Merchant feedback (2026-05-08): the static
                        // "refer to the size chart" text added a friction
                        // step ("now I have to find the size chart").
                        // The SizeGuideModal component already exists
                        // and is hooked to the "Size Guide" link by the
                        // size selector — surface a second CTA inline
                        // here so the customer can open the chart in a
                        // popup without leaving the product page.
                        <p>
                          {locale === "vi"
                            ? "Form dáng tiêu chuẩn. "
                            : "Standard fit. "}
                          <button
                            type="button"
                            onClick={() => setSizeGuideOpen(true)}
                            className="underline underline-offset-4 hover:no-underline transition-all"
                          >
                            {locale === "vi" ? "Xem bảng size" : "View size chart"}
                          </button>
                        </p>
                      )}
                      {/* Model reference — the fit model in the product photos;
                          the same line on every product. */}
                      <p className="mt-3 text-[11px] text-[#767676]">
                        {locale === "vi" ? "Người mẫu 183 cm · mặc size L" : "Model is 183 cm · wears size L"}
                      </p>

                      {/* Always-visible size-guide entry point (2026-05-14
                          per merchant feedback). The fallback `View size
                          chart` link above only fires when both fitGuide
                          fields are empty, so on most products it was
                          hidden — customers asked us to surface the size
                          chart even when the fit-bar is shown. */}
                      <p className="mt-3 text-[11px]">
                        <button
                          type="button"
                          onClick={() => setSizeGuideOpen(true)}
                          className="underline underline-offset-4 hover:no-underline transition-all text-black"
                        >
                          {locale === "vi" ? "Mở bảng size & Find My Size →" : "Open size guide & Find My Size →"}
                        </button>
                      </p>
                    </div>
                  )}
                </div>

                {/* Available in store — HCM showroom (mobile)
                    Shows stock status + address + Google Maps link.
                    Currently uses TOTAL inventory as a proxy for HCM
                    availability; per-warehouse precision needs the
                    Sapo per-warehouse sync (deferred — see commit). */}
                <div className="aritzia-accordion">
                  <button
                    type="button"
                    onClick={(e) => toggleSection("instore", e)}
                    className="aritzia-accordion-trigger"
                    aria-expanded={openSections.has("instore")}
                  >
                    <span>{locale === "vi" ? "Có tại cửa hàng" : "Available in store"}</span>
                    <span className="aritzia-accordion-icon">{openSections.has("instore") ? "−" : "+"}</span>
                  </button>
                  {openSections.has("instore") && (
                    <div className="aritzia-accordion-content space-y-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block w-2 h-2 rounded-full ${
                            product.availableAtHcm
                              ? stockStatus === "out_of_stock"
                                ? "bg-[#d4a017]"
                                : "bg-[#2e7d32]"
                              : "bg-[#c8c8c8]"
                          }`}
                          aria-hidden="true"
                        />
                        <span className="text-xs text-black">
                          {product.availableAtHcm
                            ? stockStatus === "out_of_stock"
                              ? (locale === "vi" ? "Tạm hết hàng tại cửa hàng" : "Currently out of stock at store")
                              : (locale === "vi" ? "Có sẵn tại cửa hàng" : "Available at our store")
                            : (locale === "vi"
                              ? "Sản phẩm chỉ có online — ghé cửa hàng để xem các mẫu khác"
                              : "Online only — visit our showroom for similar styles")}
                        </span>
                      </div>
                      <div>
                        <p className="text-black mb-1">{locale === "vi" ? "Diolo — TP. Hồ Chí Minh" : "Diolo — Ho Chi Minh"}</p>
                        <p>46 Võ Thị Sáu, Tân Định, Quận 1, Hồ Chí Minh</p>
                        <p className="mt-1">{locale === "vi" ? "Mở cửa hàng ngày 10:00 – 21:00" : "Open daily 10:00 – 21:00"}</p>
                      </div>
                      <a
                        href="https://www.google.com/maps/search/?api=1&query=46+V%C3%B5+Th%E1%BB%8B+S%C3%A1u%2C+T%C3%A2n+%C4%90%E1%BB%8Bnh%2C+Qu%E1%BA%ADn+1%2C+H%E1%BB%93+Ch%C3%AD+Minh"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block text-[11px] uppercase tracking-[0.08em] text-black underline hover:no-underline"
                      >
                        {locale === "vi" ? "Xem chỉ đường →" : "Get directions →"}
                      </a>
                    </div>
                  )}
                </div>

                {/* Shipping & Returns */}
                <div className="aritzia-accordion">
                  <button
                    type="button"
                    onClick={(e) => toggleSection("shipping", e)}
                    className="aritzia-accordion-trigger"
                    aria-expanded={openSections.has("shipping")}
                  >
                    <span>{locale === "vi" ? "Giao hàng & Đổi trả" : "Shipping & Returns"}</span>
                    <span className="aritzia-accordion-icon">{openSections.has("shipping") ? "−" : "+"}</span>
                  </button>
                  {openSections.has("shipping") && (
                    <div className="aritzia-accordion-content space-y-3">
                      <div>
                        <p className="text-black mb-1">{locale === "vi" ? "Giao hàng" : "Shipping"}</p>
                        <ul className="space-y-1">
                          <li>{effectiveFreeShippingThreshold === 0
                            ? (locale === "vi" ? "Miễn phí giao hàng mọi đơn hàng" : "Free shipping on all orders")
                            : effectiveFreeShippingThreshold != null
                              ? (locale === "vi" ? `Miễn phí giao hàng cho đơn từ ${formatStorefrontPrice(effectiveFreeShippingThreshold, currency || "VND")}` : `Free shipping on orders over ${formatStorefrontPrice(effectiveFreeShippingThreshold, currency || "VND")}`)
                              : (locale === "vi" ? "Tiêu chuẩn giao hàng miễn phí tính theo đơn hàng" : "Free shipping thresholds apply at checkout")}</li>
                          <li>{locale === "vi" ? "Miền Bắc: 1-2 ngày làm việc" : "Northern Vietnam: 1-2 business days"}</li>
                          <li>{locale === "vi" ? "Miền Trung: 2-3 ngày làm việc" : "Central Vietnam: 2-3 business days"}</li>
                          <li>{locale === "vi" ? "Miền Nam: 3-5 ngày làm việc" : "Southern Vietnam: 3-5 business days"}</li>
                          <li>{locale === "vi" ? "Vận chuyển qua Viettel Post" : "Shipped via Viettel Post"}</li>
                        </ul>
                      </div>
                      <div>
                        <p className="text-black mb-1">{locale === "vi" ? "Đổi hàng" : "Exchanges"}</p>
                        <ul className="space-y-1">
                          <li>{locale === "vi" ? "Đổi hàng trong 7 ngày kể từ ngày nhận" : "Exchanges within 7 days of delivery"}</li>
                          <li>{locale === "vi" ? "Sản phẩm phải còn nguyên tem mác, chưa sử dụng" : "Items must be unworn with original tags"}</li>
                          <li>{locale === "vi" ? "Liên hệ qua Instagram, Facebook hoặc email hello@diolo.com" : "Contact via Instagram, Facebook or hello@diolo.com"}</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Need help? — Messenger CTA (2026-05-14 per merchant
                    request). Customers asking sizing / fit questions on
                    chat regularly told support they "couldn't find a way
                    to contact" from the PDP — the contact line in
                    Shipping & Returns is one bullet deep in an accordion.
                    Surfacing it as its own collapsible row makes the
                    contact route a peer of Shipping/Details. Messenger
                    deep link (m.me) opens the FB Messenger app on mobile
                    and the web client otherwise. */}
                <div className="aritzia-accordion">
                  <button
                    type="button"
                    onClick={(e) => toggleSection("help", e)}
                    className="aritzia-accordion-trigger"
                    aria-expanded={openSections.has("help")}
                  >
                    <span>{locale === "vi" ? "Cần hỗ trợ?" : "Need help?"}</span>
                    <span className="aritzia-accordion-icon">{openSections.has("help") ? "−" : "+"}</span>
                  </button>
                  {openSections.has("help") && (
                    <div className="aritzia-accordion-content space-y-3">
                      <p className="text-xs text-[#555]">
                        {facebookPageUsername
                          ? (locale === "vi"
                              ? "Có thắc mắc về size, chất liệu hoặc đơn hàng? Nhắn cho đội ngũ Diolo trên Messenger — chúng tôi thường phản hồi trong vòng vài phút trong giờ làm việc."
                              : "Have a question about size, fabric, or your order? Message the Diolo team on Messenger — we usually reply within minutes during business hours.")
                          : (locale === "vi"
                              ? "Có thắc mắc về size, chất liệu hoặc đơn hàng? Đội ngũ Diolo luôn sẵn sàng hỗ trợ bạn."
                              : "Have a question about size, fabric, or your order? Our team is happy to help.")}
                      </p>
                      {facebookPageUsername ? (
                        <a
                          href={`https://m.me/${facebookPageUsername}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.08em] text-black underline hover:no-underline"
                        >
                          {locale === "vi" ? "Nhắn tin với đội ngũ Diolo →" : "Contact our team on Messenger →"}
                        </a>
                      ) : (
                        <Link
                          to="/contact"
                          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.08em] text-black underline hover:no-underline"
                        >
                          {locale === "vi" ? "Liên hệ với chúng tôi →" : "Contact us →"}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ─── Desktop Product Info (Aritzia style) ─── */}
            <div className="hidden lg:block">
              {/* Title (brand label intentionally omitted — every shopper already
                  knows the site is Diolo, so we save the vertical space) */}
              <h1 className="text-base font-normal text-black leading-snug mb-1.5">
                {displayTitle}
              </h1>

              {/* Price */}
              <div className="text-base font-normal text-black flex items-center flex-wrap gap-x-2 gap-y-1">
                {comparePrice ? (
                  <>
                    <span className="text-[#c8102e]">{formatStorefrontPrice(price, currency)}</span>
                    <span className="text-[#767676] line-through">{formatStorefrontPrice(comparePrice, currency)}</span>
                    {/* Explicit savings callout — quantifies the deal (CRO) */}
                    {Number(comparePrice) > Number(price) && (
                      <span className="px-1.5 py-0.5 bg-black text-white text-[10px] font-normal uppercase tracking-[0.08em]">
                        {locale === "vi"
                          ? `Tiết kiệm ${Math.round((1 - Number(price) / Number(comparePrice)) * 100)}%`
                          : `Save ${Math.round((1 - Number(price) / Number(comparePrice)) * 100)}%`}
                      </span>
                    )}
                  </>
                ) : (
                  <span>{formatStorefrontPrice(price, currency)}</span>
                )}
              </div>
              {taxNote && (
                <p className="mt-1 text-[11px] text-[#767676]">{taxNote}</p>
              )}
              {stampHint && (
                <p className="mt-1 text-[11px] text-[#767676] flex items-center gap-1.5"><DioloStamp className="h-3 w-auto text-black" />{stampHint}</p>
              )}

              {/* Star Rating */}
              {reviewStats.count > 0 && (
                <button
                  onClick={() => document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-2 inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                >
                  <StarRating rating={reviewStats.average} size="sm" />
                  <span className="text-xs text-[#767676] underline">
                    {reviewStats.count} {reviewStats.count === 1 ? t("reviewSingular", locale) : t("reviewPlural", locale)}
                  </span>
                </button>
              )}

              {/* Rewards Points — pin locale (see comment on the desktop
                  duplicate above for the hydration-mismatch rationale). */}
              {!REWARDS_RETIRED && rewardsPoints > 0 && (
                <p className="text-[11px] text-[#767676] mt-2">
                  {locale === "vi"
                    ? `Nhận ${rewardsPoints.toLocaleString("vi-VN")} điểm Diolo Rewards`
                    : `Earn ${rewardsPoints.toLocaleString("en-US")} Diolo Rewards points`}
                  {retailCustomer && (
                    <span className="ml-1 opacity-75">
                      {locale === "vi" ? "(thành viên)" : "(member)"}
                    </span>
                  )}
                </p>
              )}

              {/* Select Colour — Aritzia 32px circle swatches */}
              {(colors.length > 0 || siblings.length > 0) && (
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-normal text-black">
                      {locale === "vi" ? "Màu" : "Colour"}
                      <span className="text-[#767676] ml-2">
                        {hoveredColor || selectedColor || currentColor}
                      </span>
                    </p>
                    <button
                      onClick={() => toggleWishlist?.(product.id)}
                      className="flex-shrink-0 text-black hover:text-[#767676] transition-colors"
                      aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                    >
                      <svg className="w-4 h-4" fill={isWishlisted ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                      </svg>
                    </button>
                  </div>
                  <div
                    className="flex flex-wrap gap-3"
                    onMouseLeave={() => setHoveredColor(null)}
                  >
                    {colors.length > 0 ? (
                      colors.map((color) => {
                        const isSelected = selectedColor === color;
                        return (
                          <button
                            key={color}
                            onClick={() => { setSelectedColor(color); setSelectedSize(""); }}
                            onMouseEnter={() => setHoveredColor(color)}
                            data-selected={isSelected || undefined}
                            className="sf-color-swatch"
                            style={{ ['--swatch-color' as any]: getColorHex(color) }}
                            title={color}
                            aria-label={color}
                          />
                        );
                      })
                    ) : (
                      <>
                        {currentColor && (
                          <span
                            className="sf-color-swatch"
                            data-selected
                            style={{ ['--swatch-color' as any]: getColorHex(currentColor) }}
                            title={currentColor}
                            onMouseEnter={() => setHoveredColor(currentColor)}
                          />
                        )}
                        {siblings.map((sib: any) => (
                          <Link
                            key={sib.handle}
                            to={localePath(`/products/${sib.handle}`, locale)}
                            className="sf-color-swatch"
                            style={{ ['--swatch-color' as any]: getColorHex(sib.color) }}
                            title={sib.color}
                            onMouseEnter={() => setHoveredColor(sib.color)}
                          />
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Select Size — 247-style button grid (replaced the Aritzia
                  dropdown 2026-06-11): all sizes visible at once, selected
                  size fills black, OOS sizes greyed + struck through. */}
              {sizes.length > 0 && (
                <div className="mt-5 pdp-size-grid">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] font-normal text-black uppercase tracking-[0.08em]">
                      {t("selectSize", locale)}
                      {selectedSize && (
                        <span className="ml-2 text-[#737373] tracking-normal">{selectedSize}</span>
                      )}
                    </p>
                    <span className="flex items-center gap-3">
                      {/* Surface the fit-quiz where the sizing decision happens
                          (CRO 2026-06-25) — the quiz existed but was buried
                          inside the Size Guide modal. */}
                      <button
                        type="button"
                        onClick={() => {
                          setSizeGuideMode("quiz");
                          setSizeGuideOpen(true);
                        }}
                        className="text-[11px] text-black underline hover:no-underline transition-all"
                      >
                        {locale === "vi" ? "Tìm size của tôi" : "Find my size"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSizeGuideMode("chart");
                          setSizeGuideOpen(true);
                        }}
                        className="text-[11px] text-[#737373] underline hover:no-underline hover:text-black transition-all"
                      >
                        {locale === "vi" ? "Bảng size" : "Size Chart"}
                      </button>
                    </span>
                  </div>
                  <div className="grid grid-cols-5 gap-1.5" role="listbox" aria-label={t("selectSize", locale)}>
                    {sizes.map((size) => {
                      const available = isSizeAvailable(size);
                      const isSel = selectedSize === size;
                      const stock = available ? sizeStock(size) : 0;
                      const low = available && stock > 0 && stock <= LOW_STOCK_SIZE_THRESHOLD;
                      return (
                        <button
                          key={size}
                          type="button"
                          role="option"
                          aria-selected={isSel}
                          // OOS sizes stay CLICKABLE (2026-06-25): selecting one
                          // flips the buy box into the existing Sold Out +
                          // Notify-me flow, capturing demand instead of dead-
                          // ending it. Visual: struck-through, and outlined
                          // (not filled) when selected.
                          onClick={() => setSelectedSize(size)}
                          className={`h-11 border text-[12px] uppercase tracking-[0.04em] transition-colors ${
                            isSel && available
                              ? "border-black bg-black text-white"
                              : isSel
                                ? "border-black bg-white text-black line-through"
                                : available
                                  ? "border-[#e0e0e0] bg-white text-black hover:border-black"
                                  : "border-[#ededed] bg-[#fafafa] text-[#b8b8b8] line-through hover:border-[#c8c8c8]"
                          }`}
                          title={
                            !available
                              ? locale === "vi"
                                ? "Hết hàng — đăng ký nhận thông báo"
                                : "Out of stock — get notified"
                              : low
                                ? lowStockLabel(stock)
                                : undefined
                          }
                        >
                          {size}
                          {low && (
                            <span className={`block text-[9px] normal-case tracking-[0.02em] leading-none mt-0.5 ${isSel ? "text-white/70" : "text-[#6b6b6b]"}`}>
                              {lowStockLabel(stock)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quantity stepper — only meaningful once a size is selected
                  (stock cap depends on the chosen variant). Hidden for OOS
                  products since you can't add zero-stock items. */}
              {selectedSize && stockStatus !== "out_of_stock" && (
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#767676] min-w-[60px]">
                    {locale === "vi" ? "Số lượng" : "Quantity"}
                  </span>
                  <div className="inline-flex items-center border border-[#e0e0e0]">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                      aria-label={locale === "vi" ? "Giảm số lượng" : "Decrease quantity"}
                      className="w-9 h-9 flex items-center justify-center text-base hover:bg-[#f5f5f5] disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      min={1}
                      max={maxQuantity}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!Number.isFinite(n)) return setQuantity(1);
                        setQuantity(Math.max(1, Math.min(n, maxQuantity)));
                      }}
                      aria-label={locale === "vi" ? "Số lượng" : "Quantity"}
                      className="w-12 h-9 text-center text-sm border-x border-[#e0e0e0] focus:outline-none focus:bg-[#f9f9f9] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
                      disabled={quantity >= maxQuantity}
                      aria-label={locale === "vi" ? "Tăng số lượng" : "Increase quantity"}
                      className="w-9 h-9 flex items-center justify-center text-base hover:bg-[#f5f5f5] disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      +
                    </button>
                  </div>
                  {quantity >= maxQuantity && maxQuantity < 21 && (
                    <span className="text-[10px] text-[#c8102e]">
                      {locale === "vi"
                        ? `Còn lại ${maxQuantity}`
                        : `Only ${maxQuantity} left`}
                    </span>
                  )}
                </div>
              )}

              {/* Add to Bag — Aritzia full-width black. Becomes a "Sold Out"
                  label when the picked variant has no inventory; the Notify-me
                  form below replaces the action.

                  Audit 2026-05-17: wrapped in a mobile-sticky container.
                  Clarity heatmap evidence: on /products/focus-30-pastel-green
                  mobile we saw 18 page views, only 10 taps, 70% of taps on
                  the color swatch, ZERO taps on size selector or ATC button
                  — and scroll data showed 56% of users never scrolled past
                  30% of the PDP, where the ATC button sits. Users literally
                  couldn't find the buy button.

                  The wrapper sticks to the bottom of the viewport on mobile
                  (`sticky bottom-0`) and reverts to inline at `lg:` breakpoint.
                  Negative -mx-4 + px-4 reset the parent's horizontal padding
                  so the bar spans edge-to-edge on mobile, matching what
                  shoppers expect from VN ecom apps (Tiki, Shopee, etc. all
                  have a sticky bottom buy bar). Background + border give it
                  visual separation from PDP content scrolling beneath. */}
              <div className="mt-4 sticky bottom-0 lg:static -mx-4 lg:mx-0 px-4 lg:px-0 py-3 lg:py-0 bg-white lg:bg-transparent border-t lg:border-t-0 border-luxury-sand/40 z-30">
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={preLaunch || (!hasNoSizeAxis && !selectedSize) || isVariantOOS || isProductFullyOOS}
                  className="aritzia-add-to-bag"
                >
                  {preLaunch
                    ? launchLabel(locale)
                    : isProductFullyOOS || isVariantOOS
                    ? (locale === "vi" ? "Hết hàng" : "Sold Out")
                    : (hasNoSizeAxis || selectedSize)
                      ? `${locale === "vi" ? "Thêm vào giỏ" : "Add to Bag"}${quantity > 1 ? ` × ${quantity}` : ""} — ${formatStorefrontPrice(price * quantity, currency)}`
                      : t("selectSize", locale)}
                </button>

                {/* "Message us about this product" — Vietnamese-market
                    primary buying flow runs through Messenger DMs to
                    the Diolo page (>90% of revenue). This button hands
                    off the customer's product context to CS via a
                    `m.me?ref=...` deep-link that our webhook decodes
                    into an admin notification ("Customer asking about:
                    Monaco Bra Lemon"). CS skips the discovery questions
                    and answers the actual buying objection.
                    Added 2026-05-26. Button hidden if shop config
                    isn't set yet. */}
                {/* Inline (desktop only) — matches the ATC button's
                    geometry (w-[308px] h-10, text-xs font-normal,
                    no uppercase, no extra tracking) so the two stack
                    as visually balanced primary + secondary actions.
                    Inverted colours (white bg + black border) signal
                    "secondary" without the visual shouting of caps.
                    Mobile users get the icon-only Messenger button
                    in the bottom sticky bar (line ~2920). */}
                {facebookPageUsername && product.handle && (
                  <button
                    type="button"
                    onClick={() => handleMessengerClick({
                      productHandle: product.handle as string,
                      productId: product.id,
                      productTitle: product.title,
                      variantSku: selectedVariant?.sku || null,
                      facebookPageUsername,
                      instagramHandle,
                      price,
                      currency,
                      messengerLinkToken: retailCustomer?.messengerLinkToken ?? null,
                    })}
                    className="hidden lg:flex mt-2 w-[308px] max-w-full h-10 text-xs font-normal text-black bg-white border border-black hover:bg-black hover:text-white transition-colors duration-200 items-center justify-center gap-2"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 2C6.477 2 2 6.145 2 11.259c0 2.913 1.454 5.512 3.726 7.215V22l3.405-1.869c.909.252 1.871.387 2.869.387 5.523 0 10-4.145 10-9.259S17.523 2 12 2zm.994 12.461l-2.546-2.715-4.966 2.715 5.464-5.808 2.607 2.715 4.906-2.715-5.465 5.808z"/>
                    </svg>
                    {locale === "vi" ? "Nhắn tin qua Messenger" : "Message us about this"}
                  </button>
                )}
              </div>

              {/* Notify-me-when-back-in-stock — appears below the Add-to-Bag
                  button whenever the product (or chosen variant) is OOS. POSTs
                  to /api/back-in-stock; the API rate-limits per IP, upserts a
                  BackInStockNotification row, and fires a confirmation email
                  via Resend. The actual restock email is sent by the existing
                  admin endpoint /api/notify-back-in-stock when a merchandiser
                  triggers it — the auto-trigger off the inventory webhook is a
                  follow-up. */}
              {showNotifyMe && (
                <div className="mt-4 border border-[#e0e0e0] p-4">
                  {notifyStatus === "success" ? (
                    <p className="text-xs text-[#0a7d3e]" role="status">
                      {locale === "vi"
                        ? "Đã ghi nhận. Chúng tôi sẽ gửi email khi sản phẩm có hàng trở lại."
                        : "You're on the list. We'll email you the moment it's back."}
                    </p>
                  ) : (
                    <form onSubmit={handleNotifySubmit}>
                      <p className="text-[11px] uppercase tracking-[0.08em] text-black mb-3">
                        {locale === "vi" ? "Thông báo khi có hàng" : "Notify Me When Available"}
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          required
                          aria-label={locale === "vi" ? "Email của bạn" : "Your email"}
                          placeholder={locale === "vi" ? "Email của bạn" : "your@email.com"}
                          value={notifyEmail}
                          onChange={(e) => setNotifyEmail(e.target.value)}
                          disabled={notifyStatus === "loading"}
                          className="flex-1 h-10 px-3 text-xs border border-[#e0e0e0] focus:outline-none focus:border-black disabled:bg-[#f9f9f9]"
                        />
                        <button
                          type="submit"
                          disabled={notifyStatus === "loading" || !notifyEmail.trim()}
                          className="h-10 px-5 bg-black text-white text-[11px] uppercase tracking-[0.08em] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#1a1a1a] transition-colors"
                        >
                          {notifyStatus === "loading"
                            ? (locale === "vi" ? "Đang gửi..." : "Sending...")
                            : (locale === "vi" ? "Đăng ký" : "Notify Me")}
                        </button>
                      </div>
                      {notifyStatus === "error" && (
                        <p className="mt-2 text-[11px] text-[#c8102e]" role="alert">
                          {locale === "vi"
                            ? "Không gửi được. Vui lòng thử lại."
                            : (notifyError || "Couldn't submit — please try again.")}
                        </p>
                      )}
                      <p className="mt-2 text-[10px] text-[#767676]">
                        {locale === "vi"
                          ? "Bạn sẽ nhận một email duy nhất khi hàng về."
                          : "You'll receive a single email when this is restocked."}
                      </p>
                    </form>
                  )}
                </div>
              )}

              {/* Trust rows — CRO block under ATC (2026-06-25, Represent-style):
                  delivery estimate + free-shipping threshold + returns window,
                  each linking to its policy page. Estimates mirror /shipping
                  (3–5 business days VN standard) so the PDP never contradicts
                  the policy page. */}
              <div className="mt-5 border-y border-[#ececec] divide-y divide-[#ececec]">
                <Link
                  to={localePath("/shipping", locale)}
                  className="flex items-center justify-between gap-3 py-2.5 group"
                >
                  <span className="flex items-center gap-2.5 text-[11px] text-[#444]">
                    <svg className="w-4 h-4 text-black shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                    </svg>
                    {locale === "vi" ? "Giao hàng 3–5 ngày làm việc (VN)" : "Delivery in 3–5 business days (VN)"}
                  </span>
                  <span aria-hidden="true" className="text-[#999] transition-transform group-hover:translate-x-0.5">›</span>
                </Link>
                <Link
                  to={localePath("/shipping", locale)}
                  className="flex items-center justify-between gap-3 py-2.5 group"
                >
                  <span className="flex items-center gap-2.5 text-[11px] text-[#444]">
                    <svg className="w-4 h-4 text-black shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                    </svg>
                    {effectiveFreeShippingThreshold === 0
                      ? (locale === "vi" ? "Miễn phí vận chuyển mọi đơn hàng" : "Free shipping on all orders")
                      : effectiveFreeShippingThreshold != null
                        ? (locale === "vi"
                          ? `Miễn phí vận chuyển từ ${formatStorefrontPrice(effectiveFreeShippingThreshold, currency || "VND")}`
                          : `Free shipping over ${formatStorefrontPrice(effectiveFreeShippingThreshold, currency || "VND")}`)
                        : (locale === "vi" ? "Ưu đãi vận chuyển tại bước thanh toán" : "Shipping offers applied at checkout")}
                  </span>
                  <span aria-hidden="true" className="text-[#999] transition-transform group-hover:translate-x-0.5">›</span>
                </Link>
                <Link
                  to={localePath("/returns", locale)}
                  className="flex items-center justify-between gap-3 py-2.5 group"
                >
                  <span className="flex items-center gap-2.5 text-[11px] text-[#444]">
                    <svg className="w-4 h-4 text-black shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                    {locale === "vi" ? "Đổi hàng dễ dàng trong 7 ngày" : "Easy 7-day exchanges"}
                  </span>
                  <span aria-hidden="true" className="text-[#999] transition-transform group-hover:translate-x-0.5">›</span>
                </Link>
              </div>
              {stockStatus === "low_stock" && (
                <p className="mt-2 text-[11px] text-[#c8102e]">
                  {locale === "vi" ? "Số lượng có hạn" : "Limited Stock"}
                </p>
              )}

              {/* Fabric features row — Vuori-inspired (desktop). Same
                  component as mobile; renders 4 icon+label cards. */}
              <FabricFeatures fabric={product.fabric} productType={product.productType} locale={locale} />

              {/* ─── Aritzia accordions: Fabric / Details / Size & Fit / In-store / Shipping / Help ─── */}
              <div className="mt-5">
                {/* Fabric — wrapped in its own accordion 2026-05-14 to
                    match the mobile layout (merchant feedback). Skipped
                    when the product has no recognised fabric (accessories). */}
                {getFabricInfo(product.fabric) && (
                  <div className="aritzia-accordion">
                    <button
                      type="button"
                      onClick={(e) => toggleSection("fabric", e)}
                      className="aritzia-accordion-trigger"
                      aria-expanded={openSections.has("fabric")}
                    >
                      <span>{locale === "vi" ? "Chất liệu" : "Fabric"}</span>
                      <span className="text-base font-normal leading-none">{openSections.has("fabric") ? "−" : "+"}</span>
                    </button>
                    {openSections.has("fabric") && (
                      <div className="aritzia-accordion-content">
                        <FabricDetails fabric={product.fabric} locale={locale} variant="accordion" />
                      </div>
                    )}
                  </div>
                )}

                {/* Details */}
                <div className="aritzia-accordion">
                  <button
                    type="button"
                    onClick={(e) => toggleSection("details", e)}
                    className="aritzia-accordion-trigger"
                    aria-expanded={openSections.has("details")}
                  >
                    <span>{locale === "vi" ? "Chi tiết sản phẩm" : "Details"}</span>
                    <span className="text-base font-normal leading-none">{openSections.has("details") ? "−" : "+"}</span>
                  </button>
                  {openSections.has("details") && (
                    <div className="aritzia-accordion-content space-y-3">
                      {displayDescription && (
                        <div className="prose prose-sm max-w-none [&_*]:!text-xs [&_*]:!text-black [&_*]:!font-normal" dangerouslySetInnerHTML={{ __html: displayDescription }} />
                      )}
                      {(() => {
                        // Locale-aware fabric content. Vietnamese storefront
                        // prefers fabricContentVi; falls back to English copy
                        // if the translation is empty so a missing translation
                        // never blanks the composition line. EN locale stays
                        // on the English field (which is the canonical input).
                        const fabric =
                          locale === "vi"
                            ? ((product as any).fabricContentVi || product.fabricContent)
                            : product.fabricContent;
                        if (!fabric) return null;
                        return (
                          <p className="text-xs text-black">
                            <span className="font-normal">{locale === "vi" ? "Thành phần: " : "Composition: "}</span>{fabric}
                          </p>
                        );
                      })()}
                      {product.careInstructions && (
                        <p className="text-xs text-black">
                          <span className="font-normal">{locale === "vi" ? "Bảo quản: " : "Care: "}</span>{product.careInstructions}
                        </p>
                      )}
                      {product.styleNumber && (
                        <p className="text-xs text-[#767676]">
                          <span className="font-normal">{locale === "vi" ? "Mã kiểu: " : "Style: "}</span>{product.styleNumber}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Size & Fit */}
                <div className="aritzia-accordion">
                  <button
                    type="button"
                    onClick={(e) => toggleSection("fit", e)}
                    className="aritzia-accordion-trigger"
                    aria-expanded={openSections.has("fit")}
                  >
                    <span>{locale === "vi" ? "Kích thước & Form dáng" : "Size & Fit"}</span>
                    <span className="text-base font-normal leading-none">{openSections.has("fit") ? "−" : "+"}</span>
                  </button>
                  {openSections.has("fit") && (
                    <div className="aritzia-accordion-content space-y-3">
                      {product.fitGuideTop && (
                        <div>
                          <div className="flex items-center justify-between mb-1.5 text-[10px] uppercase tracking-wider text-[#767676]">
                            <span>{locale === "vi" ? "Chật" : "Runs Small"}</span>
                            <span>{locale === "vi" ? "Đúng size" : "True to Size"}</span>
                            <span>{locale === "vi" ? "Rộng" : "Runs Large"}</span>
                          </div>
                          <div className="relative h-[2px] bg-[#e0e0e0]">
                            <div
                              className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-black"
                              style={{
                                left: product.fitGuideTop === "RUNS_SMALL" ? "15%"
                                  : product.fitGuideTop === "RUNS_LARGE" ? "85%"
                                  : "50%",
                                transform: "translate(-50%, -50%)",
                              }}
                            />
                          </div>
                          <p className="text-[11px] text-black mt-2 text-center">
                            {FIT_LABELS[product.fitGuideTop] || product.fitGuideTop}
                          </p>
                        </div>
                      )}
                      {product.fitGuideBottom && (
                        <p className="text-xs text-black">{product.fitGuideBottom}</p>
                      )}
                      {!product.fitGuideTop && !product.fitGuideBottom && (
                        <p className="text-xs text-black">{locale === "vi" ? "Sản phẩm đúng size như bảng kích thước." : "True to size — refer to the size chart for measurements."}</p>
                      )}

                      {/* Always-visible size-guide entry point (desktop,
                          2026-05-14). Mirrors the mobile version. Desktop
                          also has a "Size Guide" link in the size selector
                          but customers reading the Size & Fit accordion
                          shouldn't have to scroll back up to find it. */}
                      <p className="text-[11px] pt-1">
                        <button
                          type="button"
                          onClick={() => setSizeGuideOpen(true)}
                          className="underline underline-offset-4 hover:no-underline transition-all text-black"
                        >
                          {locale === "vi" ? "Mở bảng size & Find My Size →" : "Open size guide & Find My Size →"}
                        </button>
                      </p>
                    </div>
                  )}
                </div>

                {/* Available in store — HCM showroom (desktop) */}
                <div className="aritzia-accordion">
                  <button
                    type="button"
                    onClick={(e) => toggleSection("instore", e)}
                    className="aritzia-accordion-trigger"
                    aria-expanded={openSections.has("instore")}
                  >
                    <span>{locale === "vi" ? "Có tại cửa hàng" : "Available in store"}</span>
                    <span className="text-base font-normal leading-none">{openSections.has("instore") ? "−" : "+"}</span>
                  </button>
                  {openSections.has("instore") && (
                    <div className="aritzia-accordion-content space-y-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block w-2 h-2 rounded-full ${
                            product.availableAtHcm
                              ? stockStatus === "out_of_stock"
                                ? "bg-[#d4a017]"
                                : "bg-[#2e7d32]"
                              : "bg-[#c8c8c8]"
                          }`}
                          aria-hidden="true"
                        />
                        <span className="text-xs text-black">
                          {product.availableAtHcm
                            ? stockStatus === "out_of_stock"
                              ? (locale === "vi" ? "Tạm hết hàng tại cửa hàng" : "Currently out of stock at store")
                              : (locale === "vi" ? "Có sẵn tại cửa hàng" : "Available at our store")
                            : (locale === "vi"
                              ? "Sản phẩm chỉ có online — ghé cửa hàng để xem các mẫu khác"
                              : "Online only — visit our showroom for similar styles")}
                        </span>
                      </div>
                      <div className="text-xs text-[#767676]">
                        <p className="text-black mb-1">{locale === "vi" ? "Diolo — TP. Hồ Chí Minh" : "Diolo — Ho Chi Minh"}</p>
                        <p>46 Võ Thị Sáu, Tân Định, Quận 1, Hồ Chí Minh</p>
                        <p className="mt-1">{locale === "vi" ? "Mở cửa hàng ngày 10:00 – 21:00" : "Open daily 10:00 – 21:00"}</p>
                      </div>
                      <a
                        href="https://www.google.com/maps/search/?api=1&query=46+V%C3%B5+Th%E1%BB%8B+S%C3%A1u%2C+T%C3%A2n+%C4%90%E1%BB%8Bnh%2C+Qu%E1%BA%ADn+1%2C+H%E1%BB%93+Ch%C3%AD+Minh"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block text-[11px] uppercase tracking-[0.08em] text-black underline hover:no-underline"
                      >
                        {locale === "vi" ? "Xem chỉ đường →" : "Get directions →"}
                      </a>
                    </div>
                  )}
                </div>

                {/* Shipping & Returns */}
                <div className="aritzia-accordion">
                  <button
                    type="button"
                    onClick={(e) => toggleSection("shipping", e)}
                    className="aritzia-accordion-trigger"
                    aria-expanded={openSections.has("shipping")}
                  >
                    <span>{locale === "vi" ? "Vận chuyển & Đổi trả" : "Shipping & Returns"}</span>
                    <span className="text-base font-normal leading-none">{openSections.has("shipping") ? "−" : "+"}</span>
                  </button>
                  {openSections.has("shipping") && (
                    <div className="aritzia-accordion-content space-y-3">
                      <div>
                        <p className="text-xs text-black mb-1">{locale === "vi" ? "Vận chuyển" : "Shipping"}</p>
                        <ul className="text-xs text-[#767676] space-y-1 list-disc ml-4">
                          <li>{effectiveFreeShippingThreshold === 0
                            ? (locale === "vi" ? "Miễn phí giao hàng mọi đơn hàng" : "Free shipping on all orders")
                            : effectiveFreeShippingThreshold != null
                              ? (locale === "vi" ? `Miễn phí giao hàng cho đơn từ ${formatStorefrontPrice(effectiveFreeShippingThreshold, currency || "VND")}` : `Free shipping on orders over ${formatStorefrontPrice(effectiveFreeShippingThreshold, currency || "VND")}`)
                              : (locale === "vi" ? "Tiêu chuẩn giao hàng miễn phí tính theo đơn hàng" : "Free shipping thresholds apply at checkout")}</li>
                          <li>{locale === "vi" ? "Miền Bắc: 1-2 ngày làm việc" : "Northern Vietnam: 1-2 business days"}</li>
                          <li>{locale === "vi" ? "Miền Trung: 2-3 ngày làm việc" : "Central Vietnam: 2-3 business days"}</li>
                          <li>{locale === "vi" ? "Miền Nam: 3-5 ngày làm việc" : "Southern Vietnam: 3-5 business days"}</li>
                          <li>{locale === "vi" ? "Vận chuyển qua Viettel Post" : "Shipped via Viettel Post"}</li>
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs text-black mb-1">{locale === "vi" ? "Đổi hàng" : "Exchanges"}</p>
                        <ul className="text-xs text-[#767676] space-y-1 list-disc ml-4">
                          <li>{locale === "vi" ? "Đổi hàng trong 7 ngày kể từ ngày nhận" : "Exchanges within 7 days of delivery"}</li>
                          <li>{locale === "vi" ? "Sản phẩm phải còn nguyên tem mác, chưa sử dụng" : "Items must be unworn with original tags"}</li>
                          <li>{locale === "vi" ? "Liên hệ qua Instagram, Facebook hoặc email hello@diolo.com" : "Contact via Instagram, Facebook or hello@diolo.com"}</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Need help? — Messenger CTA (desktop, 2026-05-14).
                    Mirrors the mobile accordion. */}
                <div className="aritzia-accordion">
                  <button
                    type="button"
                    onClick={(e) => toggleSection("help", e)}
                    className="aritzia-accordion-trigger"
                    aria-expanded={openSections.has("help")}
                  >
                    <span>{locale === "vi" ? "Cần hỗ trợ?" : "Need help?"}</span>
                    <span className="text-base font-normal leading-none">{openSections.has("help") ? "−" : "+"}</span>
                  </button>
                  {openSections.has("help") && (
                    <div className="aritzia-accordion-content space-y-3">
                      <p className="text-xs text-[#555]">
                        {facebookPageUsername
                          ? (locale === "vi"
                              ? "Có thắc mắc về size, chất liệu hoặc đơn hàng? Nhắn cho đội ngũ Diolo trên Messenger — chúng tôi thường phản hồi trong vòng vài phút trong giờ làm việc."
                              : "Have a question about size, fabric, or your order? Message the Diolo team on Messenger — we usually reply within minutes during business hours.")
                          : (locale === "vi"
                              ? "Có thắc mắc về size, chất liệu hoặc đơn hàng? Đội ngũ Diolo luôn sẵn sàng hỗ trợ bạn."
                              : "Have a question about size, fabric, or your order? Our team is happy to help.")}
                      </p>
                      {facebookPageUsername ? (
                        <a
                          href={`https://m.me/${facebookPageUsername}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.08em] text-black underline hover:no-underline"
                        >
                          {locale === "vi" ? "Nhắn tin với đội ngũ Diolo →" : "Contact our team on Messenger →"}
                        </a>
                      ) : (
                        <Link
                          to="/contact"
                          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.08em] text-black underline hover:no-underline"
                        >
                          {locale === "vi" ? "Liên hệ với chúng tôi →" : "Contact us →"}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Desktop Sticky Add-to-Bag Bar (Aritzia style) ─── */}
      <div
        className={`hidden lg:flex fixed bottom-0 left-0 right-0 z-[60] bg-white border-t border-[#e0e0e0] transition-transform duration-300 ${
          showDesktopStickyBar ? "translate-y-0" : "translate-y-full"
        }`}
        aria-hidden={!showDesktopStickyBar}
      >
        <div className="mx-auto max-w-[1440px] w-full px-6 lg:px-10 py-3 flex items-center gap-6">
          {/* Thumbnail */}
          <div className="w-12 h-16 flex-shrink-0 bg-[#f5f5f5] overflow-hidden">
            {images[0] && (
              <img
                src={images[0].url}
                alt={displayTitle}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}
          </div>

          {/* Title + colour */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-normal text-black truncate">{displayTitle}</p>
            {(selectedColor || currentColor) && (
              <p className="text-[11px] text-[#767676] truncate">
                {selectedColor || currentColor}
              </p>
            )}
          </div>

          {/* Price */}
          <div className="text-xs font-normal text-black flex-shrink-0">
            {comparePrice ? (
              <>
                <span className="text-[#c8102e]">{formatStorefrontPrice(price, currency)}</span>
                <span className="text-[#767676] line-through ml-2">
                  {formatStorefrontPrice(comparePrice, currency)}
                </span>
              </>
            ) : (
              <span>{formatStorefrontPrice(price, currency)}</span>
            )}
          </div>

          {/* Size trigger — scrolls user to the inline size grid */}
          {sizes.length > 0 && (
            <button
              type="button"
              onClick={() => {
                document
                  .querySelector(".pdp-size-grid")
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
              className="aritzia-size-trigger w-[200px] flex-shrink-0"
              data-empty={!selectedSize || undefined}
            >
              <span>
                {selectedSize || t("selectSize", locale)}
              </span>
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            </button>
          )}

          {/* Add to Bag — same handler as inline button. Reflects OOS state
              by mirroring the main button's label so the sticky bar can't be
              used to bypass the Notify-me flow. */}
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={preLaunch || (!hasNoSizeAxis && !selectedSize) || isVariantOOS || isProductFullyOOS}
            className="aritzia-add-to-bag w-[260px] flex-shrink-0"
          >
            {preLaunch
              ? launchLabel(locale)
              : isProductFullyOOS || isVariantOOS
              ? (locale === "vi" ? "Hết hàng" : "Sold Out")
              : (hasNoSizeAxis || selectedSize)
                ? `${locale === "vi" ? "Thêm vào giỏ" : "Add to Bag"} — ${formatStorefrontPrice(price, currency)}`
                : t("selectSize", locale)}
          </button>
        </div>
      </div>

      {/* ─── Aritzia-style Mobile Sticky Bar ─── */}
      {/* Backdrop for size bottom sheet */}
      {openDropdown === "size" && !cartOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-[69] transition-opacity"
          onClick={() => setOpenDropdown(null)}
        />
      )}

      {/* Size bottom sheet */}
      {openDropdown === "size" && sizes.length > 0 && !cartOpen && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white z-[71] animate-slide-up max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-4 border-b border-[#e0e0e0]">
            <h3 className="text-xs font-normal text-black uppercase tracking-[0.08em]">
              {t("selectSize", locale)}
            </h3>
            <button
              onClick={() => setOpenDropdown(null)}
              aria-label="Close"
              className="text-black"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="py-2">
            {sizes.map((size) => {
              const available = isSizeAvailable(size);
              const isSelected = selectedSize === size;
              const stock = available ? sizeStock(size) : 0;
              const low = available && stock > 0 && stock <= LOW_STOCK_SIZE_THRESHOLD;
              return (
                <button
                  key={size}
                  // OOS sizes clickable (2026-06-25): selecting one closes the
                  // sheet and puts the buy bar into Sold Out state while the
                  // in-page notify-me form captures the restock intent.
                  onClick={() => {
                    setSelectedSize(size);
                    setOpenDropdown(null);
                  }}
                  className={`w-full flex items-center justify-between px-6 py-3 text-xs font-normal transition-colors ${
                    isSelected
                      ? "bg-[#f4f4f4] text-black"
                      : available
                        ? "text-black hover:bg-[#f4f4f4]"
                        : "text-[#999] line-through hover:bg-[#f9f9f9]"
                  }`}
                >
                  <span>{size}</span>
                  {!available ? (
                    <span className="text-[10px] text-[#999] uppercase tracking-[0.05em]">
                      {locale === "vi" ? "Hết hàng — nhận thông báo" : "Sold Out — Get Notified"}
                    </span>
                  ) : low ? (
                    <span className="text-[10px] tracking-wide text-[#6b6b6b]">
                      {lowStockLabel(stock)}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className={`lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#e0e0e0] z-[70] transition-transform duration-300 ${cartOpen ? "translate-y-full" : "translate-y-0"}`}>
        <div className="px-4 py-3 flex items-center gap-2">
          {/* Size trigger button — narrowed to w-28 (112px) on 2026-05-26
              so the Messenger icon button has room to live next to the
              wishlist heart without squeezing the Add to Bag button's
              price label. Showed an "M" or "Chọn size" placeholder, both
              fit comfortably in 112px. */}
          {sizes.length > 0 ? (
            <button
              onClick={() => setOpenDropdown(openDropdown === "size" ? null : "size")}
              className="flex-none w-28 h-11 flex items-center justify-between px-3 border border-black bg-white text-xs font-normal text-black rounded-none"
            >
              <span className="truncate">
                {selectedSize || t("selectSize", locale)}
              </span>
              <svg className="w-3 h-3 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
          ) : null}

          {/* Add to Bag — black with price (Aritzia: solid black always, no
              color change). Mobile sticky bar mirrors OOS state from the main
              button so the user can't tap "Add" on a sold-out variant.
              The Notify-me form lives in the main scroll content above. */}
          <button
            onClick={handleAddToCart}
            disabled={preLaunch || (sizes.length > 0 && !selectedSize) || isVariantOOS || isProductFullyOOS}
            className={`flex-1 h-11 px-3 flex items-center justify-center gap-2 text-xs font-normal rounded-none transition-colors ${
              (isProductFullyOOS || isVariantOOS)
                ? "bg-[#e5e5e5] text-[#999] cursor-not-allowed"
                : sizes.length === 0 || selectedSize
                  ? "bg-black text-white hover:bg-[#1a1a1a]"
                  : "bg-[#e5e5e5] text-[#999] cursor-not-allowed"
            }`}
          >
            <span className="whitespace-nowrap">
              {preLaunch
                ? launchLabel(locale)
                : isProductFullyOOS || isVariantOOS
                ? (locale === "vi" ? "Hết hàng" : "Sold Out")
                : sizes.length > 0 && !selectedSize
                  ? (locale === "vi" ? "Chọn size" : "Select a Size")
                  : (locale === "vi" ? "Thêm vào giỏ" : "Add to Bag")}
            </span>
            {!preLaunch && !(isProductFullyOOS || isVariantOOS) && (
              <span className="whitespace-nowrap">— {formatStorefrontPrice(price, currency)}</span>
            )}
          </button>

          {/* Messenger — mobile-only icon button matching the wishlist
              heart's size + shape. Tap → opens m.me deep link with the
              same ref payload as the desktop button. 2026-05-26: lives
              in the sticky bar so the customer can always reach CS in
              one tap regardless of scroll position. */}
          {facebookPageUsername && product.handle && (
            <button
              type="button"
              onClick={() => handleMessengerClick({
                productHandle: product.handle as string,
                productId: product.id,
                productTitle: product.title,
                variantSku: selectedVariant?.sku || null,
                facebookPageUsername,
                instagramHandle,
                price,
                currency,
                messengerLinkToken: retailCustomer?.messengerLinkToken ?? null,
              })}
              className="flex-none w-11 h-11 flex items-center justify-center border border-[#e0e0e0] rounded-none"
              aria-label={locale === "vi" ? "Nhắn tin qua Messenger" : "Message us about this product"}
            >
              <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2C6.477 2 2 6.145 2 11.259c0 2.913 1.454 5.512 3.726 7.215V22l3.405-1.869c.909.252 1.871.387 2.869.387 5.523 0 10-4.145 10-9.259S17.523 2 12 2zm.994 12.461l-2.546-2.715-4.966 2.715 5.464-5.808 2.607 2.715 4.906-2.715-5.465 5.808z"/>
              </svg>
            </button>
          )}

          {/* Wishlist */}
          <button
            onClick={() => toggleWishlist?.(product.id)}
            className="flex-none w-11 h-11 flex items-center justify-center border border-[#e0e0e0] rounded-none"
            aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
          >
            <svg
              className={`w-4 h-4 ${isWishlisted ? "text-red-500" : "text-black"}`}
              fill={isWishlisted ? "currentColor" : "none"}
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Complete the Look — Kith-style with size selection + Quick View.
          Position: directly below the Shipping & Returns accordion (which
          ends at the close of the desktop product-info column above) and
          ABOVE Customer Reviews. Merchant request 2026-05-01: customers
          should see companion-piece suggestions before reading reviews. */}
      {completeTheLook.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 pt-8 pb-16 lg:pt-10 lg:pb-20 border-t border-[#e0e0e0]">
          <div className="text-center mb-8 lg:mb-10">
            <h2 className="sf-section-title">{t("completeTheLook", locale)}</h2>
            <p className="sf-section-subtitle">{t("pairItWith", locale)}</p>
          </div>
          <div className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2">
            {(completeTheLook as any[])
              // Skip products whose intl price resolution returned
              // null (no target MarketPrice + no SG fallback). Same
              // policy as the YMAL list above.
              .filter((item: any) => item.retailPrice != null || item.variants?.[0]?.price)
              // 2026-05-12: bumped from `.slice(0, 6)` to `.slice(0, 10)`
              // so the accessory recommendations (white sock + court-
              // sport visor hats) appended to completeTheLook in the
              // loader don't get squeezed out by a strong outfit-match
              // set. Carousel is horizontally scrollable so extra
              // items just extend the scroll length.
              .slice(0, 10)
              .map((item: any) => {
              // Defensive: when `item.variants` is undefined or null on
              // certain product records, `item.variants?.map(...).filter(...)`
              // short-circuits to undefined and the downstream
              // `[...new Set(undefined)]` returns an empty array — fine in
              // V8/SpiderMonkey but at least one engine (Safari/WebKit)
              // surfaces it via a downstream destructure assertion as
              // "Right side of assignment cannot be destructured" during
              // SSR (REMIX-8, 2026-05-11). Falling back to `[]` before the
              // chain guarantees an array contract regardless.
              const itemVariants: any[] = item.variants ?? [];
              const rawOpt1 = [...new Set(itemVariants.map((v: any) => v.option1).filter(Boolean))] as string[];
              const rawOpt2 = [...new Set(itemVariants.map((v: any) => v.option2).filter(Boolean))] as string[];
              const itemOpt1IsSize = rawOpt2.length === 0 && rawOpt1.length > 0 && rawOpt1.every((v: string) => v in SIZE_ORDER);
              const itemColors = itemOpt1IsSize ? [] : rawOpt1;
              const itemSizes = (itemOpt1IsSize ? rawOpt1 : rawOpt2).sort((a, b) => {
                const orderA = SIZE_ORDER[a] ?? 50;
                const orderB = SIZE_ORDER[b] ?? 50;
                if (orderA !== orderB) return orderA - orderB;
                return a.localeCompare(b);
              });
              const itemPrice = item.retailPrice ? Number(item.retailPrice) : Number(item.variants?.[0]?.price || 0);

              return (
                <CompleteTheLookCard
                  key={item.id}
                  item={item}
                  colors={itemColors}
                  sizes={itemSizes}
                  price={itemPrice}
                  currency={currency}
                  addToCart={addToCart}
                  option1IsSize={itemOpt1IsSize}
                  locale={locale}
                  onQuickView={() => setQuickViewProduct(item)}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* ─── Mid-PDP editorial band (CRO 2026-06-25) — an activity-keyed
          full-bleed brand moment between the buy area and reviews, mirroring
          the editorial rhythm of premium activewear PDPs. Interim imagery
          from /editorial; swaps automatically once real photography lands. */}
      {(() => {
        const activityKey = (product.activity || "").toLowerCase();
        const band =
          activityKey === "court"
            ? { img: "/editorial/night-fence.jpg", eyebrow: locale === "vi" ? "Sinh ra cho sân đấu" : "Built for the court", title: locale === "vi" ? "Kiểm soát từng pha bóng" : "Control Every Point" }
            : activityKey === "run"
              ? { img: "/editorial/night-shadow.jpg", eyebrow: locale === "vi" ? "Sinh ra để chạy" : "Built to run", title: locale === "vi" ? "Nhẹ hơn. Nhanh hơn." : "Lighter. Faster." }
              : activityKey === "lounge"
                ? { img: "/editorial/night-wall.jpg", eyebrow: locale === "vi" ? "Cho ngày nghỉ" : "For rest days", title: locale === "vi" ? "Thoải mái đúng nghĩa" : "Comfort, Considered" }
                : { img: "/editorial/night-bag.jpg", eyebrow: locale === "vi" ? "Sinh ra để tập luyện" : "Built to train", title: locale === "vi" ? "Bền bỉ qua từng buổi tập" : "Session After Session" };
        return (
          <section className="relative h-[46svh] min-h-[320px] overflow-hidden bg-black">
            <img
              src={band.img}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
            />
            <div className="absolute inset-0 bg-black/35" />
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center">
              <p className="text-[12px] font-normal text-white/90">{band.eyebrow}</p>
              <h2 className="df-headline mt-1 text-[26px] text-white sm:text-[32px]">{band.title}</h2>
              {product.fabricContent && (
                <p className="mt-3 max-w-md text-[12px] leading-relaxed text-white/75">
                  {product.fabricContent}
                </p>
              )}
            </div>
          </section>
        );
      })()}

      {/* ─── Reviews Section ─── */}
      <section id="reviews" className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 pt-8 pb-16 lg:pt-12 lg:pb-20 border-t border-[#e0e0e0]">
        <div className="flex flex-col md:flex-row md:items-start gap-10">
          {/* Left: Summary */}
          <div className="md:w-72 flex-shrink-0">
            <h2 className="text-xs font-normal uppercase tracking-[0.1em] text-black mb-4">{t("customerReviews", locale)}</h2>
            {reviewStats.count > 0 ? (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl font-normal text-black">{reviewStats.average.toFixed(1)}</span>
                  <div>
                    <StarRating rating={reviewStats.average} size="md" />
                    <p className="text-xs text-[#767676] mt-0.5">
                      Based on {reviewStats.count} {reviewStats.count === 1 ? "review" : "reviews"}
                    </p>
                  </div>
                </div>
                {/* Rating distribution */}
                <div className="space-y-1.5">
                  {reviewStats.distribution.map(({ star, count }) => (
                    <div key={star} className="flex items-center gap-2">
                      <span className="text-xs text-[#767676] w-3">{star}</span>
                      <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <div className="flex-1 h-1 bg-[#e0e0e0] overflow-hidden">
                        <div
                          className="h-full bg-black transition-all duration-300"
                          style={{ width: reviewStats.count > 0 ? `${(count / reviewStats.count) * 100}%` : "0%" }}
                        />
                      </div>
                      <span className="text-xs text-[#767676] w-6 text-right">{count}</span>
                    </div>
                  ))}
                </div>
                {/* T4.5: fit-guide row from real product data (no fake bars) */}
                {fitLabel && (
                  <div className="mt-6 pt-4 border-t border-[#e0e0e0]">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] uppercase tracking-[0.1em] text-[#767676] w-14 flex-shrink-0">
                        Fit
                      </span>
                      <span className="text-xs text-black flex-1">{fitLabel}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-[#767676]">No reviews yet.</p>
            )}
            <button
              onClick={() => setShowReviewForm(!showReviewForm)}
              className="sf-btn-secondary sf-btn-small w-full mt-6"
            >
              {locale === "vi" ? "Viết đánh giá" : "Write a Review"}
            </button>
          </div>

          {/* Right: Review List */}
          <div className="flex-1 min-w-0">
            {/* Review Search + Sort Controls */}
            {reviews.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#767676]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search reviews..."
                    value={reviewSearch}
                    onChange={(e) => setReviewSearch(e.target.value)}
                    className="w-full border border-[#e0e0e0] bg-white pl-9 pr-3 py-2.5 text-xs text-black placeholder:text-[#767676] focus:border-black focus:outline-none rounded-none"
                  />
                </div>
                <select
                  value={reviewSort}
                  onChange={(e) => setReviewSort(e.target.value)}
                  className="border border-[#e0e0e0] bg-white px-3 py-2.5 text-xs text-black focus:border-black focus:outline-none appearance-none cursor-pointer min-w-[160px] rounded-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23767676' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
                >
                  <option value="verified">Verified First</option>
                  <option value="newest">Newest</option>
                  <option value="highest">Highest Rated</option>
                  <option value="lowest">Lowest Rated</option>
                </select>
              </div>
            )}

            {/* Review Form */}
            {showReviewForm && (
              <div className="mb-8 p-6 border border-[#e0e0e0] bg-white">
                <h3 className="text-xs font-normal uppercase tracking-[0.1em] text-black mb-4">Write a Review</h3>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    fetch("/api/reviews", {
                      method: "POST",
                      body: JSON.stringify({
                        productId: product.id,
                        customerName: formData.get("name"),
                        rating: Number(formData.get("rating")),
                        title: formData.get("title"),
                        body: formData.get("body"),
                      }),
                      headers: { "Content-Type": "application/json" },
                    }).then(() => {
                      setShowReviewForm(false);
                      window.location.reload();
                    });
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="text-xs text-[#767676] block mb-1">Your Name</label>
                    <input
                      name="name"
                      required
                      className="w-full border border-[#e0e0e0] bg-white px-3 py-2.5 text-xs text-black focus:border-black focus:outline-none rounded-none"
                      placeholder="Jane D."
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#767676] block mb-1">Rating</label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <label key={n} className="cursor-pointer">
                          <input type="radio" name="rating" value={n} required className="sr-only peer" />
                          <svg
                            className="w-6 h-6 text-[#e0e0e0] peer-checked:text-black hover:text-[#767676] transition-colors"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#767676] block mb-1">Review Title</label>
                    <input
                      name="title"
                      className="w-full border border-[#e0e0e0] bg-white px-3 py-2.5 text-xs text-black focus:border-black focus:outline-none rounded-none"
                      placeholder="Great product!"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#767676] block mb-1">Your Review</label>
                    <textarea
                      name="body"
                      rows={4}
                      className="w-full border border-[#e0e0e0] bg-white px-3 py-2.5 text-xs text-black focus:border-black focus:outline-none resize-none rounded-none"
                      placeholder="Share your experience..."
                    />
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" className="sf-btn-filled sf-btn-small">
                      Submit Review
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowReviewForm(false)}
                      className="text-xs text-[#767676] underline hover:text-black"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Reviews List */}
            {reviews.length > 0 ? (
              <div className="space-y-6">
                {filteredReviews.length === 0 && reviewSearch.trim() && (
                  <div className="text-center py-8">
                    <p className="text-xs text-[#767676]">
                      No reviews matching &ldquo;{reviewSearch}&rdquo;
                    </p>
                    <button
                      onClick={() => setReviewSearch("")}
                      className="text-xs text-black underline hover:no-underline mt-1"
                    >
                      Clear search
                    </button>
                  </div>
                )}
                {filteredReviews.map((review) => (
                  <div key={review.id} className="pb-6 border-b border-[#e0e0e0] last:border-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <StarRating rating={review.rating} size="sm" />
                        {review.title && (
                          <h4 className="text-xs font-normal text-black mt-1">{review.title}</h4>
                        )}
                      </div>
                      {/* Pin timezone to fix SSR/CSR hydration mismatch.
                          Without an explicit timeZone, server (Node, UTC
                          on Fly) and client (user browser, typically
                          Asia/Ho_Chi_Minh UTC+7) format dates in different
                          local times — a review created at 17:00 UTC May 7
                          renders as "May 7" on the server and "May 8" in
                          the browser. React detects the text mismatch on
                          ONE review row, then discards the entire reviews
                          subtree and re-renders it client-only — which is
                          why this single line was responsible for ~40
                          cascading hydration warnings (#418/#425/#423) on
                          every PDP load. Diolo is a VN merchant, so we pin
                          to Asia/Ho_Chi_Minh — the customer-perceived
                          calendar date stays correct. */}
                      <span className="text-[10px] text-[#767676] whitespace-nowrap">
                        {new Date(review.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          timeZone: "Asia/Ho_Chi_Minh",
                        })}
                      </span>
                    </div>
                    {review.body && (
                      <p className="text-xs text-black mt-2 leading-relaxed">{review.body}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-black">{review.customerName}</span>
                      {review.isVerified && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-black bg-[#f4f4f4] px-2 py-0.5">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Verified Buyer
                        </span>
                      )}
                    </div>
                    {/* Was this helpful? — community voting (CRO 2026-06-25).
                        localStorage guards one vote per review per browser;
                        counts update optimistically from the endpoint. */}
                    <div className="flex items-center justify-end gap-3 mt-3">
                      <span className="text-[10px] text-[#767676]">
                        {locale === "vi" ? "Đánh giá này có hữu ích?" : "Was this review helpful?"}
                      </span>
                      {(["up", "down"] as const).map((dir) => {
                        const base = dir === "up" ? (review as any).helpfulCount ?? 0 : (review as any).notHelpfulCount ?? 0;
                        const delta = reviewVotes[review.id]?.[dir] ?? 0;
                        const voted = reviewVotes[review.id]?.voted;
                        return (
                          <button
                            key={dir}
                            type="button"
                            onClick={() => handleReviewVote(review.id, dir)}
                            disabled={!!voted}
                            aria-label={dir === "up" ? "Helpful" : "Not helpful"}
                            className={`inline-flex items-center gap-1 text-[11px] transition-colors ${
                              voted === dir ? "text-black" : voted ? "text-[#c8c8c8]" : "text-[#767676] hover:text-black"
                            } disabled:cursor-default`}
                          >
                            <svg className={`w-3.5 h-3.5 ${dir === "down" ? "rotate-180" : ""}`} fill={voted === dir ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75 2.25 2.25 0 012.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H14.23c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H6.633" />
                            </svg>
                            {base + delta}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-xs text-[#767676] mb-2">Be the first to review this product.</p>
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="text-xs text-black underline hover:no-underline"
                >
                  Write a Review
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* You May Also Like + Quick View */}
      {youMayAlsoLike.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 pt-8 pb-16 lg:pt-10 lg:pb-20 border-t border-[#e0e0e0]">
          <div className="text-center mb-8 lg:mb-10">
            <h2 className="sf-section-title">{t("youMayAlsoLike", locale)}</h2>
            <p className="sf-section-subtitle">{t("similarStyles", locale)}</p>
          </div>
          <div className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2">
            {(youMayAlsoLike as any[])
              // Audit 2026-04-25: skip products whose intl price
              // resolution returned null (no target MarketPrice and
              // no SG fallback). Better to render a shorter rail
              // than show wrong-currency prices.
              .filter((item: any) => item.retailPrice != null || item.variants?.[0]?.price)
              .slice(0, 8)
              .map((item: any) => {
              const ymalPrice = item.retailPrice ? Number(item.retailPrice) : Number(item.variants?.[0]?.price || 0);
              return (
                <div key={item.id} className="flex-none w-[200px] sm:w-[230px] snap-start group/ymal">
                  <div className="relative">
                    <Link to={localePath(`/products/${item.handle}`, locale)}>
                      <div className="aspect-[1920/2623] bg-[#f5f5f5] overflow-hidden">
                        {resolveFeaturedImage(item) ? (
                          <img
                            src={resolveFeaturedImage(item)}
                            alt={titleForLocale(item.title, locale, (item as any).titleEn ?? null)}
                            className="w-full h-full object-cover group-hover/ymal:scale-[1.02] transition-transform duration-500"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-[#f5f5f5]" />
                        )}
                      </div>
                    </Link>
                    {/* Quick View on hover */}
                    <button
                      onClick={() => setQuickViewProduct(item)}
                      className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white text-black text-[10px] font-normal uppercase tracking-[0.1em] px-4 py-2 opacity-0 group-hover/ymal:opacity-100 transition-all duration-200 hover:bg-black hover:text-white"
                    >
                      {locale === "vi" ? "Xem nhanh" : "Quick View"}
                    </button>
                  </div>
                  <Link to={`/products/${item.handle}`} className="block mt-2">
                    <h4 className="text-xs font-normal text-black truncate">{titleForLocale(item.title, locale, (item as any).titleEn ?? null)}</h4>
                    <p className="text-xs text-black mt-0.5">{formatStorefrontPrice(ymalPrice, currency)}</p>
                  </Link>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Shop More {Category} */}
      {(product.productType || product.activity || (product as any).collection) && (
        <section className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 pt-5 pb-10 border-t border-[#e0e0e0]">
          <p className="text-xs text-black mb-3">
            {locale === "vi" ? "Xem thêm" : "Shop More"}{" "}
            {product.productType || (product as any).collection?.name || ""}
          </p>
          <div className="flex flex-wrap gap-2">
            {product.productType && (
              <Link
                to={localePath(`/products?type=${encodeURIComponent(product.productType)}`, locale)}
                className="text-xs text-black border border-[#e0e0e0] px-3 py-1.5 hover:border-black transition-colors"
              >
                {locale === "vi" ? "Tất cả" : "All"} {product.productType}
              </Link>
            )}
            {product.activity && (
              <Link
                to={localePath(`/products?activity=${encodeURIComponent(product.activity)}`, locale)}
                className="text-xs text-black border border-[#e0e0e0] px-3 py-1.5 hover:border-black transition-colors"
              >
                {product.activity.split(",").map((a: string) => a.trim().charAt(0).toUpperCase() + a.trim().slice(1)).join(", ")}
              </Link>
            )}
            {(product as any).collection && (
              <Link
                to={localePath("/collections", locale)}
                className="text-xs text-black border border-[#e0e0e0] px-3 py-1.5 hover:border-black transition-colors"
              >
                {(product as any).collection.name}
              </Link>
            )}
          </div>
        </section>
      )}

      {/* Recently Viewed Products */}
      <RecentlyViewed currentProductId={product.id} locale={locale} currency={currency} marketId={(market as any)?.marketId} />

      {/* Quick View Modal */}
      {quickViewProduct && (
        <QuickViewModal
          product={quickViewProduct}
          addToCart={addToCart}
          locale={locale}
          currency={currency}
          onClose={() => setQuickViewProduct(null)}
        />
      )}

      {/* Spacer for mobile sticky CTA (two rows: dropdowns + add to cart) */}
      <div className="lg:hidden h-28" />

      {/* Size guide + Find My Size quiz modal — opens via the
          "Size Guide" link next to the size selector. (added 2026-04-30)
          customChart / customImage threaded through 2026-05-08 so each
          PDP can show a per-product chart instead of the generic default. */}
      <SizeGuideModal
        open={sizeGuideOpen}
        onClose={() => setSizeGuideOpen(false)}
        locale={locale}
        productType={product.productType}
        customChart={(product as any).sizeChartData ?? null}
        customImage={(product as any).sizeChartImage ?? null}
        initialMode={sizeGuideMode}
      />

      {/* Full-screen image lightbox (mobile gallery tap-to-zoom) */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

// ─── Desktop Image Gallery (Aritzia: vertical scroll + thumbnail strip) ─── //
function DesktopImageGallery({
  images,
  productTitle,
}: {
  images: { url: string; altText?: string }[];
  productTitle: string;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);

  // IntersectionObserver to track which image is currently in viewport
  useEffect(() => {
    if (typeof window === "undefined" || images.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the highest intersection ratio
        let bestEntry: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) {
              bestEntry = entry;
            }
          }
        }
        if (bestEntry) {
          const idx = Number((bestEntry.target as HTMLElement).dataset.idx);
          if (!Number.isNaN(idx)) setActiveIndex(idx);
        }
      },
      { threshold: [0.4, 0.6, 0.8], rootMargin: "-20% 0px -20% 0px" },
    );
    imageRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [images.length]);

  const scrollToImage = useCallback((idx: number) => {
    const el = imageRefs.current[idx];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  if (images.length === 0) {
    return <div className="hidden lg:block aspect-[1920/2623] bg-[#f5f5f5]" />;
  }

  return (
    <div className="hidden lg:flex gap-3">
      {/* Vertical thumbnail strip — sticky on the left */}
      {images.length > 1 && (
        <div className="w-[60px] flex-shrink-0 sticky top-24 self-start space-y-2 max-h-[calc(100vh-8rem)] overflow-y-auto scrollbar-hide">
          {images.map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollToImage(i)}
              className={`w-full aspect-[1920/2623] overflow-hidden bg-[#f5f5f5] transition-opacity ${
                i === activeIndex ? "opacity-100 ring-1 ring-black" : "opacity-60 hover:opacity-100"
              }`}
              aria-label={`View image ${i + 1}`}
            >
              <img
                src={img.url}
                alt={img.altText || productTitle}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {/* Main vertical image stack with hover zoom */}
      <div className="flex-1 space-y-1">
        {images.map((img, i) => (
          <div
            key={i}
            ref={(el) => (imageRefs.current[i] = el)}
            data-idx={i}
            className="aspect-[1920/2623] bg-[#f5f5f5] overflow-hidden cursor-zoom-in"
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * 100;
              const y = ((e.clientY - rect.top) / rect.height) * 100;
              e.currentTarget.style.setProperty("--zoom-x", `${x}%`);
              e.currentTarget.style.setProperty("--zoom-y", `${y}%`);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.removeProperty("--zoom-x");
              e.currentTarget.style.removeProperty("--zoom-y");
            }}
          >
            <img
              src={img.url}
              alt={img.altText || productTitle}
              className="w-full h-full object-cover transition-transform duration-200 hover:scale-150"
              style={{ transformOrigin: "var(--zoom-x, 50%) var(--zoom-y, 50%)" }}
              loading={i < 2 ? "eager" : "lazy"}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Complete the Look Card (Kith-style with size selection + Quick View) ─── //
function CompleteTheLookCard({
  item,
  colors,
  sizes,
  price,
  currency,
  addToCart,
  option1IsSize = false,
  locale = "vi",
  onQuickView,
}: {
  item: any;
  colors: string[];
  sizes: string[];
  price: number;
  currency: string;
  addToCart: (item: CartItem) => void;
  option1IsSize?: boolean;
  locale?: Locale;
  onQuickView?: () => void;
}) {
  const [selectedColor, setSelectedColor] = useState(colors[0] || "");
  const [selectedSize, setSelectedSize] = useState("");
  const [added, setAdded] = useState(false);

  const isSizeAvailable = (size: string) => {
    if (option1IsSize) {
      return item.variants?.some((v: any) => v.option1 === size && v.inventoryQuantity > 0);
    }
    return item.variants?.some(
      (v: any) => v.option1 === selectedColor && v.option2 === size && v.inventoryQuantity > 0
    );
  };

  const handleAdd = () => {
    const variant = option1IsSize
      ? item.variants?.find((v: any) => v.option1 === selectedSize)
      : item.variants?.find((v: any) => v.option1 === selectedColor && v.option2 === selectedSize);
    if (!variant) return;

    const imgData = item.images as { url: string; color?: string }[] | null;
    const imageUrl = imgData?.[0]?.url || item.featuredImageUrl;

    addToCart({
      variantId: variant.id,
      productHandle: item.handle || item.id,
      productTitle: item.title,
      productTitleEn: (item as any).titleEn ?? null,
      variantTitle: selectedColor ? `${selectedColor} / ${selectedSize}` : selectedSize,
      color: selectedColor,
      size: selectedSize,
      price: Number(variant.price || price),
      imageUrl,
      quantity: 1,
      productId: item.id,
      maxQuantity: variant.inventoryQuantity,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="flex-none w-[260px] sm:w-[280px] snap-start bg-white group/card">
      {/* Product Image + Quick View overlay */}
      <div className="relative">
        <Link to={localePath(`/products/${item.handle}`, locale)} className="block">
          <div className="aspect-[1920/2623] bg-[#f5f5f5] overflow-hidden">
            {resolveFeaturedImage(item) ? (
              <img
                src={resolveFeaturedImage(item)}
                alt={titleForLocale(item.title, locale, (item as any).titleEn ?? null)}
                className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-[#f5f5f5]" />
            )}
          </div>
        </Link>
        {/* Quick View button on hover */}
        {onQuickView && (
          <button
            onClick={(e) => { e.preventDefault(); onQuickView(); }}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white text-black text-[10px] font-normal uppercase tracking-[0.1em] px-4 py-2 opacity-0 group-hover/card:opacity-100 transition-all duration-200 hover:bg-black hover:text-white"
          >
            {locale === "vi" ? "Xem nhanh" : "Quick View"}
          </button>
        )}
      </div>
      {/* Info + Size Selection */}
      <div className="pt-3">
        <Link to={`/products/${item.handle}`} className="block">
          <h4 className="text-xs font-normal text-black truncate">{titleForLocale(item.title, locale, (item as any).titleEn ?? null)}</h4>
          <p className="text-xs text-black mt-0.5">{formatStorefrontPrice(price, currency)}</p>
        </Link>

        {/* Color swatches (compact) */}
        {colors.length > 1 && (
          <div className="flex gap-2 mt-2">
            {colors.slice(0, 5).map((color) => (
              <button
                key={color}
                onClick={() => { setSelectedColor(color); setSelectedSize(""); }}
                className="sf-color-swatch-sm"
                data-selected={selectedColor === color ? "true" : undefined}
                style={{ ['--swatch-color' as any]: getColorHex(color) }}
                title={color}
              />
            ))}
          </div>
        )}

        {/* Size pills (inline scroll) */}
        {sizes.length > 0 && (
          <div className="flex gap-1.5 mt-2 overflow-x-auto scrollbar-hide">
            {sizes.map((size) => {
              const available = isSizeAvailable(size);
              return (
                <button
                  key={size}
                  onClick={() => { if (available) setSelectedSize(size); }}
                  className={`flex-none min-w-[2rem] h-8 flex items-center justify-center border text-[11px] font-normal transition-all ${
                    selectedSize === size
                      ? "bg-black text-white border-black"
                      : available
                        ? "border-[#e0e0e0] text-black hover:border-black"
                        : "border-[#e0e0e0] text-[#c8c8c8] line-through cursor-not-allowed"
                  }`}
                  disabled={!available}
                >
                  {size}
                </button>
              );
            })}
          </div>
        )}

        {/* Add button */}
        <button
          onClick={handleAdd}
          disabled={!selectedSize}
          className={`w-full mt-2.5 h-9 text-[11px] font-normal transition-all ${
            added
              ? "bg-emerald-600 text-white"
              : selectedSize
                ? "bg-black text-white hover:bg-[#1a1a1a]"
                : "bg-[#f4f4f4] text-[#767676] cursor-not-allowed"
          }`}
        >
          {added
            ? t("added", locale)
            : selectedSize
              ? t("addToCartLabel", locale)
              : t("selectASizeLabel", locale)}
        </button>
      </div>
    </div>
  );
}

// ─── Recently Viewed Products ─── //
function RecentlyViewed({ currentProductId, locale = "vi", currency, marketId }: { currentProductId: string; locale?: Locale; currency: string; marketId?: string }) {
  const [items, setItems] = useState<Array<{
    id: string;
    handle: string;
    title: string;
    featuredImageUrl: string | null;
    price: number;
    /** Per-item currency overlay from /api/recently-viewed-filter. */
    currency?: string;
  }>>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored: any[] = JSON.parse(
          localStorage.getItem("eql_recently_viewed") || "[]",
        );
        const cached = stored
          .filter((p: any) => p.id !== currentProductId)
          .slice(0, 8);
        if (cached.length === 0) {
          setItems([]);
          return;
        }

        // Audit 2026-04-25: same-page fix as the shared
        // RecentlyViewed component. localStorage stores VND prices
        // from the moment the user added a product to history; if
        // the user later visits via a UK / SG VPN we'd render those
        // VND numbers labelled with the foreign currency symbol.
        // Hit /api/recently-viewed-filter to overlay fresh
        // market-adjusted prices before showing the rail.
        const res = await fetch("/api/recently-viewed-filter", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ids: cached.map((p) => p.id),
            ...(marketId ? { marketId } : {}),
          }),
        });
        const data: {
          availableIds?: string[];
          prices?: Record<
            string,
            { price: number; currency: string; comparePrice?: number }
          >;
        } = await res.json();
        if (cancelled) return;

        const availableSet = new Set(data.availableIds || []);
        const priceMap = data.prices || {};
        // For international visitors, hide products the API couldn't
        // price (no target MarketPrice + no SG fallback) — better an
        // empty rail than wrong-currency labels on cached VN amounts.
        const isIntl = !!currency && currency !== "VND";
        const refreshed = cached
          .filter((p) => availableSet.has(p.id))
          .map((p) => {
            const fresh = priceMap[p.id];
            if (fresh) return { ...p, price: fresh.price, currency: fresh.currency };
            if (isIntl) return null;
            return p;
          })
          .filter((p): p is typeof cached[number] => p !== null);
        setItems(refreshed);
      } catch {
        // localStorage / fetch failure → fall back to silent skip.
        // Better to render nothing than the wrong currency.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentProductId, marketId]);

  if (items.length === 0) return null;

  return (
    <section className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 pt-8 pb-16 lg:pt-10 lg:pb-20 border-t border-[#e0e0e0]">
      <div className="text-center mb-6 lg:mb-8">
        <h2 className="sf-section-title">{locale === "vi" ? "Xem gần đây" : "Recently Viewed"}</h2>
      </div>
      <div className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2">
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/products/${item.handle}`}
            className="flex-none w-[180px] sm:w-[200px] snap-start group/rv"
          >
            <div className="aspect-3/4 bg-[#f5f5f5] overflow-hidden rounded">
              {item.featuredImageUrl ? (
                <img
                  src={resolveFeaturedImage(item as any) || fixImgUrl(item.featuredImageUrl)}
                  alt={titleForLocale(item.title, locale, (item as any).titleEn ?? null)}
                  className="w-full h-full object-cover group-hover/rv:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
              ) : (
                <div className="w-full h-full bg-[#f5f5f5]" />
              )}
            </div>
            <h4 className="text-xs font-medium text-black mt-2 truncate">{titleForLocale(item.title, locale, (item as any).titleEn ?? null)}</h4>
            <p className="text-xs text-[#767676] mt-0.5">{formatStorefrontPrice(item.price, item.currency || currency)}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ─── Quick View Modal ─── //
function QuickViewModal({
  product,
  addToCart,
  locale = "vi",
  currency,
  onClose,
}: {
  product: any;
  addToCart: (item: CartItem) => void;
  locale?: Locale;
  currency: string;
  onClose: () => void;
}) {
  // Defensive fallback matches the CompleteTheLook block above — see
  // the comment there for the SSR destructure-failure rationale.
  const productVariants: any[] = product.variants ?? [];
  const rawOpt1 = [...new Set(productVariants.map((v: any) => v.option1).filter(Boolean))] as string[];
  const rawOpt2 = [...new Set(productVariants.map((v: any) => v.option2).filter(Boolean))] as string[];
  const opt1IsSize = rawOpt2.length === 0 && rawOpt1.length > 0 && rawOpt1.every((v: string) => v in SIZE_ORDER);
  const qvColors = opt1IsSize ? [] : rawOpt1;
  const qvSizes = (opt1IsSize ? rawOpt1 : rawOpt2).sort((a, b) => {
    const orderA = SIZE_ORDER[a] ?? 50;
    const orderB = SIZE_ORDER[b] ?? 50;
    if (orderA !== orderB) return orderA - orderB;
    return a.localeCompare(b);
  });

  const [selectedColor, setSelectedColor] = useState(qvColors[0] || "");
  const [selectedSize, setSelectedSize] = useState("");
  const [added, setAdded] = useState(false);

  const qvPrice = product.retailPrice ? Number(product.retailPrice) : Number(product.variants?.[0]?.price || 0);

  const isSizeAvailable = (size: string) => {
    if (opt1IsSize) {
      return product.variants?.some((v: any) => v.option1 === size && v.inventoryQuantity > 0);
    }
    return product.variants?.some(
      (v: any) => v.option1 === selectedColor && v.option2 === size && v.inventoryQuantity > 0
    );
  };

  const handleAdd = () => {
    const variant = opt1IsSize
      ? product.variants?.find((v: any) => v.option1 === selectedSize)
      : product.variants?.find((v: any) => v.option1 === selectedColor && v.option2 === selectedSize);
    if (!variant) return;

    const rawImgs = product.images as (string | { url: string })[] | null;
    const firstImg = rawImgs?.[0];
    const imageUrl = (typeof firstImg === "string" ? firstImg : firstImg?.url) || product.featuredImageUrl;

    addToCart({
      variantId: variant.id,
      productHandle: product.handle || product.id,
      productTitle: product.title,
      productTitleEn: (product as any).titleEn ?? null,
      variantTitle: selectedColor ? `${selectedColor} / ${selectedSize}` : selectedSize,
      color: selectedColor,
      size: selectedSize,
      price: Number(variant.price || qvPrice),
      imageUrl,
      quantity: 1,
      productId: product.id,
      maxQuantity: variant.inventoryQuantity,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto shadow-xl animate-slide-up">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white text-black rounded-full shadow-sm"
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Product Image */}
        <div className="aspect-[4/3] bg-[#f5f5f5] overflow-hidden">
          <img
            src={resolveFeaturedImage(product as any) || fixImgUrl(product.featuredImageUrl)}
            alt={product.title}
            className="w-full h-full object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        </div>

        {/* Product Info */}
        <div className="p-5">
          <Link
            to={`/products/${product.handle}`}
            className="text-base font-medium text-black hover:underline"
            onClick={onClose}
          >
            {product.title}
          </Link>
          <p className="text-sm text-[#767676] mt-1">{formatStorefrontPrice(qvPrice, currency)}</p>

          {/* Color swatches */}
          {qvColors.length > 1 && (
            <div className="flex gap-2 mt-3">
              {qvColors.map((color) => (
                <button
                  key={color}
                  onClick={() => { setSelectedColor(color); setSelectedSize(""); }}
                  className={`w-6 h-6 rounded-full border-2 transition-colors ${
                    selectedColor === color ? "border-black" : "border-[#e0e0e0]"
                  }`}
                  style={{ backgroundColor: getColorHex(color) }}
                  title={color}
                />
              ))}
            </div>
          )}

          {/* Size selection */}
          {qvSizes.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-[#767676] mb-2">
                {t("selectSizeLabel", locale)}
              </p>
              <div className="flex gap-2 flex-wrap">
                {qvSizes.map((size) => {
                  const available = isSizeAvailable(size);
                  return (
                    <button
                      key={size}
                      onClick={() => { if (available) setSelectedSize(size); }}
                      disabled={!available}
                      className={`min-w-[2.5rem] h-9 flex items-center justify-center border text-xs font-medium transition-all ${
                        selectedSize === size
                          ? "bg-black text-white border-black"
                          : available
                            ? "border-[#e0e0e0] text-black hover:border-black"
                            : "border-[#e0e0e0] text-[#c0c0c0] line-through cursor-not-allowed"
                      }`}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add to Bag */}
          <button
            onClick={handleAdd}
            disabled={!selectedSize}
            className={`w-full mt-4 py-3 text-xs font-normal uppercase tracking-[0.1em] transition-all rounded-none ${
              added
                ? "bg-emerald-600 text-white"
                : selectedSize
                  ? "bg-black text-white hover:bg-[#1a1a1a]"
                  : "bg-[#e5e5e5] text-[#767676] cursor-not-allowed"
            }`}
          >
            {added
              ? (locale === "vi" ? "\u2713 Đã thêm" : "\u2713 Added to Bag")
              : selectedSize
                ? (t("addToCartLabel", locale))
                : t("selectSize", locale)}
          </button>

          {/* View full details link */}
          <Link
            to={`/products/${product.handle}`}
            onClick={onClose}
            className="block text-center text-xs text-[#767676] underline hover:text-black mt-3"
          >
            {locale === "vi" ? "Xem chi tiết" : "View Full Details"}
          </Link>
        </div>
      </div>
    </div>
  );
}

export { RouteErrorBoundary as ErrorBoundary } from "~/components/RouteErrorBoundary";
